import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, Plus, X, Edit2, User, Video, Download, Trash, Zap, Mic, Type, RefreshCw, Upload, Filter, Triangle} from 'react-feather';
import TextareaAutosize from 'react-textarea-autosize';
import api from './api';
import './BackOffice.css';
import { trackEvent } from './utils/amplitude';
import { debounce } from 'lodash';
import { useDarkMode } from './DarkModeContext';

const BackOffice = () => {
    const { darkMode } = useDarkMode();
    
    // Default voice options for when user doesn't have a voice
    const DEFAULT_VOICES = [
        { id: 'st7NwhTPEzqo2riw7qWC', name: 'Blondie Radio Host' },
        { id: '6OzrBCQf8cjERkYgzSg8', name: 'Young Jamal' },
        { id: 'f5HLTX707KIM4SzJYzSz', name: "Hey it's Brad" }
    ];
    
    // Add CSS for tick slider
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .tick-slider-container {
                position: relative;
                width: 100%;
            }
            .tick-marks {
                position: absolute;
                width: 100%;
                display: flex;
                justify-content: space-evenly;
                pointer-events: none;
                margin-left: 3px;
                margin-right: 3px;
            }
            .tick {
                width: 2px;
                height: 3px;
                background-color: #fff;
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            document.head.removeChild(style);
        };
    }, []);
    const [activeTab, setActiveTab] = useState('users'); // 'users' or 'videos'
    const [users, setUsers] = useState([]);
    const [videos, setVideos] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [tagInputs, setTagInputs] = useState({}); // Map of videoId to tag input value
    const [newCut, setNewCut] = useState({ name: '', url: '' });
    const [errors, setErrors] = useState([]);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    const [userContentTab, setUserContentTab] = useState('videos'); // 'videos', 'generated', 'profile', or 'studio'
    const [generatedVideos, setGeneratedVideos] = useState([]);
    const [generatedVideosCache, setGeneratedVideosCache] = useState({}); // Cache by userId
    const [scriptsCache, setScriptsCache] = useState({}); // Cache by userId
    const [isDraggingGenerated, setIsDraggingGenerated] = useState(false);
    const generatedFileInputRef = useRef(null);
    
    // Video upload state for admin
    const [videoUploadProgress, setVideoUploadProgress] = useState({});
    const [videoUploadStats, setVideoUploadStats] = useState({
        totalFiles: 0,
        currentFileIndex: 0,
        completedFiles: 0,
        failedFiles: 0,
        currentFileName: ''
    });
    const [isDraggingVideos, setIsDraggingVideos] = useState(false);
    const videoFileInputRef = useRef(null);
    const [editingUser, setEditingUser] = useState(null);
    
    // User filtering state
    const [showUserFilters, setShowUserFilters] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState(() => {
        const saved = localStorage.getItem('backoffice_selected_users');
        return saved ? JSON.parse(saved) : [];
    });
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [userEditForm, setUserEditForm] = useState({
        currentSituation: '',
        shortTermGoals: '',
        longTermGoals: '',
        websiteUrl: '',
        currentRole: '',
        companyPurpose: '',
        targetAudience: '',
        howToBePerceived: '',
        currentMarketingStrategy: '',
        contentIdeas: '',
        instagramUrl: '',
        inspiringBrands: '',
        competitors: '',
        targetChallenge: '',
        additionalInformation: '',
        customFontUrl: '',
        contentStrategy: '',
        socialMedia: {
            mainPlatform: 'instagram',
            instagram: {
                handle: '',
                currentFollowers: 0,
                targetFollowers: 0
            },
            tiktok: {
                handle: '',
                currentFollowers: 0,
                targetFollowers: 0
            },
            youtube: {
                handle: '',
                currentFollowers: 0,
                targetFollowers: 0
            }
        },
        videoProduction: {
            currentVideosPerMonth: 0,
            targetVideosPerMonth: 0
        }
    });

    // Viral hooks list for script generation
    const VIRAL_HOOKS = [
        "There are 2 ways to [solve challenge]. First [old way], or you can [describe value proposition].",
        "If you haven't already heard, [describe way to achieve target goal]. Here are example of [examples]",
        "I hate to break it to you but you're doing X wrong.",
        "I hate X, and here's why.",
        "I hate X, and here's why",
        "Here's why you're stuck",
        "If you want to achieve X, you should try this instead",
        "Here's what X doesn't want you to know about this topic",
        "Stop doing these three things immediately",
        "You're risking X by ignoring this",
        "This should be illegal. [describe how good the solution is]",
        "This one thing is ruining your chances of X",
        "Here's why X is setting you up to fail",
        "Stop wasting your time. X doesn't work anymore",
        "Why you should never… when doing …",
        "300 days ago, I was…",
        "Today I woke up…",
        "Day # since I started doing X…",
        "If I were to create X today, here is how I would do it",
        "I literally used to [something negative] until …",
        "I do not believe in [common belief], I believe in [belief linked to the value proposition]",
        "Do you ever [insert situation], yeah well, that's my job.",
        "Proof that [product or service] can totally transform [what it transforms]",
        "Is it possible to go from [before state] to [after state] by [using product or service]",
        "We finally built it. For the last months, we've been building [product and value proposition]",
        "Let me show y'all how to [insert value proposition]",
        "This is the life of [insert target audience]. But that was until [insert product or service]",
        "Last night at around [hour], I [activity]",
        "Yesterday I [activity] in [time it takes] thanks to [value proposition or service]",
        "This is the ultimate [service, product, or value proposition]",
        "Today I woke up and realized that…",
        "I always wondered what it was like to…",
        "I'm on a journey to find the best…",
        "I want to [action] but I don't have [something that would be needed and hard to get, but that can be replaced by the solution]. It turns out [explain why it's not needed when the solution exists]",
        "It all started when…",
        "This is probably the coolest thing I ever done…",
        "I don't have a backup plan so this kind of needs to work",
        "I will let you in on a secret (insert verb) is the easiest thing in the world",
        "if you (insert action) like this, then you're doing it wrong",
        "You are not (insert bad label), you are not (insert bad label), you just need to (insert action).",
        "It would take (time) to (action) without (product or service)",
        "If you've never heard of [brand], you're life is about to change.",
        "This is a great trick.",
        "Here is a hack to (result)",
        "Why does nobody talk about…",
        "Give me 30 seconds and I will show you how to [expected result]",
        "This is the 2025 way to [action] in order to [result]",
        "Why you should never… when doing…",
        "I went from X to Y.",
        "This is wild",
        "I wasted months doing (previous way to do it)…"
    ];

    // Function to randomly select 16 hooks from the viral hooks list
    const getRandomHooks = () => {
        const shuffled = [...VIRAL_HOOKS].sort(() => 0.5 - Math.random());
        return shuffled.slice(0, 16);
    };

    const [scriptHooks, setScriptHooks] = useState(getRandomHooks());
    const [generatedScripts, setGeneratedScripts] = useState([]);
    const [isGeneratingScripts, setIsGeneratingScripts] = useState(false);
    const [generatingVoiceovers, setGeneratingVoiceovers] = useState(new Set());
    const [generatingVideos, setGeneratingVideos] = useState(new Set());

    // Add new state variable for waiting messages
    const [currentWaitingMessage, setCurrentWaitingMessage] = useState(0);

    // Add state for Auto-Cut toggle
    const [autoCutEnabled, setAutoCutEnabled] = useState(() => {
        const saved = localStorage.getItem('admin-auto-cut-enabled');
        return saved ? JSON.parse(saved) : false;
    });
    const [includePublishedContext, setIncludePublishedContext] = useState(() => {
        const saved = localStorage.getItem('admin-include-published-context');
        return saved ? JSON.parse(saved) : false;
    });
    const [creativityLevel, setCreativityLevel] = useState(() => {
        const saved = localStorage.getItem('admin-creativity-level');
        return saved ? JSON.parse(saved) : 3; // Default to middle (0.8 temperature)
    });
    const [dragCreativityValue, setDragCreativityValue] = useState(3);
    const [isGeneratingContentStrategy, setIsGeneratingContentStrategy] = useState(false);
    const [selectedDefaultVoice, setSelectedDefaultVoice] = useState(DEFAULT_VOICES[0].id); // Default to first voice

    // Voice generation settings
    const [voiceSettingsOpen, setVoiceSettingsOpen] = useState(() => {
        const saved = localStorage.getItem('admin-voice-settings-open');
        return saved ? JSON.parse(saved) : false;
    });
    const [voiceStyle, setVoiceStyle] = useState(() => {
        const saved = localStorage.getItem('admin-voice-style');
        return saved ? JSON.parse(saved) : 2; // Default to middle (0.1)
    });
    const [voiceStability, setVoiceStability] = useState(() => {
        const saved = localStorage.getItem('admin-voice-stability');
        return saved ? JSON.parse(saved) : 2; // Default to middle (0.6)
    });
    const [voiceSimilarity, setVoiceSimilarity] = useState(() => {
        const saved = localStorage.getItem('admin-voice-similarity');
        return saved ? JSON.parse(saved) : 2; // Default to middle (0.5)
    });
    const [dragVoiceStyle, setDragVoiceStyle] = useState(2);
    const [dragVoiceStability, setDragVoiceStability] = useState(2);
    const [dragVoiceSimilarity, setDragVoiceSimilarity] = useState(2);

    // Add waiting messages array
    const WAITING_MESSAGES = [
        "Analysing the brief...",
        "Connecting it to target and solution...",
        "Coming up with creative scripts...",
        "Making it attention-grabbing...",
        "Final touch..."
    ];

    // Function to get status message based on progress
    const getVideoGenerationStatusMessage = (progress) => {
        if (progress <= 5) return "Initializing video generation...";
        if (progress <= 15) return "Processing voiceover audio...";
        if (progress <= 25) return "Selecting video clips...";
        if (progress <= 40) return "Downloading video content...";
        if (progress <= 45) return "Preparing video sequence...";
        if (progress <= 60) return "Combining video clips...";
        if (progress <= 70) return "Analyzing video dimensions...";
        if (progress <= 80) return "Cropping to vertical format...";
        if (progress <= 85) return "Generating captions...";
        if (progress <= 90) return "Adding subtitles to video...";
        if (progress <= 95) return "Syncing voiceover with video...";
        if (progress <= 99) return "Finalizing and uploading...";
        return "Video generation complete!";
    };

    // Add a debounced save function
    const debouncedSave = useRef(null);
    const debouncedSaveAlt = useRef(null);
    useEffect(() => {
        debouncedSave.current = debounce(async (scriptId, content, description, contentAlt) => {
            try {
                await api.updateScript(scriptId, content, description, contentAlt);
                // Don't update local state here to avoid overwriting user's current edits
                // The local state is already updated optimistically in the onChange handler
            } catch (error) {
                setErrors(['Failed to save script changes: ' + (error.response?.data?.error || error.message)]);
                // On error, we could potentially revert the local state, but for now just show the error
            }
        }, 1000); // Wait 1 second after the last change before saving

        debouncedSaveAlt.current = debounce(async (scriptId, contentAlt) => {
            try {
                await api.updateScript(scriptId, undefined, undefined, contentAlt);
            } catch (error) {
                setErrors(['Failed to save script changes: ' + (error.response?.data?.error || error.message)]);
            }
        }, 1000);

        return () => {
            if (debouncedSave.current) {
                debouncedSave.current.cancel();
            }
            if (debouncedSaveAlt.current) {
                debouncedSaveAlt.current.cancel();
            }
        };
    }, []);

    // Save includePublishedContext to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('admin-include-published-context', JSON.stringify(includePublishedContext));
    }, [includePublishedContext]);

    // Save creativityLevel to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('admin-creativity-level', JSON.stringify(creativityLevel));
    }, [creativityLevel]);

    // Initialize drag value when creativityLevel changes
    useEffect(() => {
        setDragCreativityValue(creativityLevel);
    }, [creativityLevel]);

    // Save voice settings to localStorage
    useEffect(() => {
        localStorage.setItem('admin-voice-settings-open', JSON.stringify(voiceSettingsOpen));
    }, [voiceSettingsOpen]);

    useEffect(() => {
        localStorage.setItem('admin-voice-style', JSON.stringify(voiceStyle));
        setDragVoiceStyle(voiceStyle);
    }, [voiceStyle]);

    useEffect(() => {
        localStorage.setItem('admin-voice-stability', JSON.stringify(voiceStability));
        setDragVoiceStability(voiceStability);
    }, [voiceStability]);

    useEffect(() => {
        localStorage.setItem('admin-voice-similarity', JSON.stringify(voiceSimilarity));
        setDragVoiceSimilarity(voiceSimilarity);
    }, [voiceSimilarity]);

    // Handle creativity slider drag with magnetic effect
    const handleCreativitySliderDrag = (e) => {
        const currentValue = parseFloat(e.target.value);
        const nearestInt = Math.round(currentValue);
        const distance = currentValue - nearestInt;
        
        let adjustedValue;
        
        if (Math.abs(distance) < 0.5) {
            // Close to an integer - apply magnetic effect
            adjustedValue = nearestInt + (distance * 0.4); // Slow down movement
        } else {
            // Far from integers - move freely
            adjustedValue = currentValue;
        }
        
        setDragCreativityValue(adjustedValue);
    };

    // Handle final value when released
    const handleCreativitySliderRelease = () => {
        const finalValue = Math.round(dragCreativityValue);
        setCreativityLevel(finalValue);
        setDragCreativityValue(finalValue);
        trackEvent('admin_creativity_level_changed', {
            newValue: finalValue,
            previousValue: creativityLevel
        });
    };

    // Voice slider handlers
    const handleVoiceSliderDrag = (type, value) => {
        const currentValue = parseFloat(value);
        const nearestInt = Math.round(currentValue);
        const distance = currentValue - nearestInt;
        
        let adjustedValue;
        if (Math.abs(distance) < 0.5) {
            adjustedValue = nearestInt + (distance * 0.4); // Magnetic effect
        } else {
            adjustedValue = currentValue;
        }
        
        if (type === 'style') setDragVoiceStyle(adjustedValue);
        else if (type === 'stability') setDragVoiceStability(adjustedValue);
        else if (type === 'similarity') setDragVoiceSimilarity(adjustedValue);
    };

    const handleVoiceSliderRelease = (type) => {
        let finalValue, previousValue;
        
        if (type === 'style') {
            finalValue = Math.round(dragVoiceStyle);
            previousValue = voiceStyle;
            setVoiceStyle(finalValue);
            setDragVoiceStyle(finalValue);
        } else if (type === 'stability') {
            finalValue = Math.round(dragVoiceStability);
            previousValue = voiceStability;
            setVoiceStability(finalValue);
            setDragVoiceStability(finalValue);
        } else if (type === 'similarity') {
            finalValue = Math.round(dragVoiceSimilarity);
            previousValue = voiceSimilarity;
            setVoiceSimilarity(finalValue);
            setDragVoiceSimilarity(finalValue);
        }
        
        trackEvent('admin_voice_setting_changed', {
            type,
            newValue: finalValue,
            previousValue: previousValue
        });
    };

    // Handle script version selection
    const handleScriptVersionChange = async (scriptId, selectedVersion) => {
        try {
            await api.updateScriptVersion(scriptId, selectedVersion);
            
            // Update local state
            setGeneratedScripts(prev => prev.map(script => 
                script._id === scriptId 
                    ? { ...script, selectedVersion }
                    : script
            ));
            
            trackEvent('admin_script_version_changed', {
                scriptId,
                selectedVersion
            });
        } catch (error) {
            setErrors(['Failed to update script version: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Helper functions for voice settings
    const getVoiceSettingLabel = (type, value) => {
        const roundedValue = Math.round(value);
        const labels = {
            style: ['Natural', 'Expressive', 'Dramatic'],
            stability: ['Variable', 'Balanced', 'Consistent'],
            similarity: ['Creative', 'Balanced', 'Accurate']
        };
        return labels[type][roundedValue - 1] || labels[type][1];
    };

    const getVoiceSettingValue = (type, level) => {
        const values = {
            style: [0, 0.1, 0.2],
            stability: [0.5, 0.6, 0.7],
            similarity: [0.4, 0.5, 0.6]
        };
        return values[type][level - 1] || values[type][1];
    };

    // Generate content strategy using OpenAI
    const handleGenerateContentStrategy = async () => {
        if (!selectedUser) return;
        
        setIsGeneratingContentStrategy(true);
        setErrors([]);
        
        try {
            const response = await api.generateContentStrategy({
                userId: selectedUser._id
            });
            
            // Update the form with the generated strategy
            setUserEditForm(prev => ({
                ...prev,
                contentStrategy: response.data.contentStrategy
            }));
            
            // Also update the selectedUser so it's visible in normal view
            setSelectedUser(prev => ({
                ...prev,
                profile: {
                    ...prev.profile,
                    contentStrategy: response.data.contentStrategy
                }
            }));
            
            trackEvent('admin_content_strategy_generated', {
                userId: selectedUser._id
            });
        } catch (error) {
            setErrors(['Failed to generate content strategy: ' + (error.response?.data?.error || error.message)]);
            trackEvent('admin_content_strategy_generation_error', {
                userId: selectedUser._id,
                error: error.message
            });
        } finally {
            setIsGeneratingContentStrategy(false);
        }
    };

    // Fetch users and videos on component mount
    useEffect(() => {
        if (isAuthenticated) {
            fetchUsers();
            fetchVideos();
            trackEvent('admin_dashboard_loaded');
        }
    }, [isAuthenticated]);

    // Apply filters when users data changes
    useEffect(() => {
        if (users.length > 0) {
            const filtered = applyUserFilters(users);
            setFilteredUsers(filtered);
        }
    }, [users, selectedUserIds, userSearchQuery]);

    // Fetch users
    const fetchUsers = async () => {
        try {
            setIsLoading(true);
            const response = await api.getAdminUsers();
            trackEvent('admin_users_fetched', {
                count: response.data.length
            });
            setUsers(response.data);
            
            // Apply current filters to new user data
            const filtered = applyUserFilters(response.data);
            setFilteredUsers(filtered);
        } catch (error) {
            trackEvent('admin_users_fetch_error', {
                error: error.message
            });
            setErrors(['Failed to fetch users: ' + (error.response?.data?.error || error.message)]);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch videos
    const fetchVideos = async () => {
        try {
            setIsLoading(true);
            const response = await api.getAdminVideos();
            trackEvent('admin_videos_fetched', {
                count: response.data.length
            });
            setVideos(response.data);
        } catch (error) {
            trackEvent('admin_videos_fetch_error', {
                error: error.message
            });
            setErrors(['Failed to fetch videos: ' + (error.response?.data?.error || error.message)]);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch generated videos for a user (with caching)
    const fetchGeneratedVideos = async (userId, forceRefresh = false) => {
        // Check cache first
        if (!forceRefresh && generatedVideosCache[userId]) {
            setGeneratedVideos(generatedVideosCache[userId]);
            return;
        }

        try {
            const response = await api.getUserGeneratedVideos(userId);
            const videos = response.data;
            
            // Update cache and state
            setGeneratedVideosCache(prev => ({
                ...prev,
                [userId]: videos
            }));
            setGeneratedVideos(videos);
        } catch (error) {
            setErrors(['Failed to fetch generated videos: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Handle status change
    const handleStatusChange = async (videoId, newStatus) => {
        trackEvent('admin_video_status_change_attempt', {
            videoId,
            newStatus
        });

        try {
            await api.updateVideoStatus(videoId, newStatus);
            trackEvent('admin_video_status_change_success', {
                videoId,
                newStatus
            });
            
            // Update local state
            setVideos(prev => prev.map(video => 
                video._id === videoId ? { ...video, status: newStatus } : video
            ));
            
            if (selectedUser) {
                setSelectedUser(prev => ({
                    ...prev,
                    videos: prev.videos.map(video =>
                        video._id === videoId ? { ...video, status: newStatus } : video
                    )
                }));
            }
        } catch (error) {
            trackEvent('admin_video_status_change_error', {
                videoId,
                newStatus,
                error: error.message
            });
            setErrors(['Failed to update video status: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Handle tag addition
    const handleAddTag = async (videoId) => {
        const tagValue = tagInputs[videoId];
        if (!tagValue?.trim()) return;

        trackEvent('admin_tag_add_attempt', {
            videoId,
            tag: tagValue.trim()
        });

        const trimmedTag = tagValue.trim();

        // Optimistically update the UI
        setVideos(prev => prev.map(video => 
            video._id === videoId 
                ? { ...video, tags: [...video.tags, trimmedTag] }
                : video
        ));
        
        if (selectedUser) {
            setSelectedUser(prev => ({
                ...prev,
                videos: prev.videos.map(video =>
                    video._id === videoId
                        ? { ...video, tags: [...video.tags, trimmedTag] }
                        : video
                )
            }));
        }

        // Clear the input immediately
        setTagInputs(prev => ({
            ...prev,
            [videoId]: ''
        }));

        try {
            await api.addVideoTag(videoId, trimmedTag);
            trackEvent('admin_tag_add_success', {
                videoId,
                tag: trimmedTag
            });
            // No need to update UI again since we did it optimistically
        } catch (error) {
            trackEvent('admin_tag_add_error', {
                videoId,
                tag: trimmedTag,
                error: error.message
            });
            // If there's an error, remove the tag from the UI
            setVideos(prev => prev.map(video => 
                video._id === videoId
                    ? { ...video, tags: video.tags.filter(tag => tag !== trimmedTag) }
                    : video
            ));
            
            if (selectedUser) {
                setSelectedUser(prev => ({
                    ...prev,
                    videos: prev.videos.map(video =>
                        video._id === videoId
                            ? { ...video, tags: video.tags.filter(tag => tag !== trimmedTag) }
                            : video
                    )
                }));
            }
            setErrors(['Failed to add tag: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Handle tag removal
    const handleRemoveTag = async (videoId, tagToRemove) => {
        trackEvent('admin_tag_remove_attempt', {
            videoId,
            tag: tagToRemove
        });

        // Optimistically update the UI
        setVideos(prev => prev.map(video => 
            video._id === videoId
                ? { ...video, tags: video.tags.filter(tag => tag !== tagToRemove) }
                : video
        ));
        
        if (selectedUser) {
            setSelectedUser(prev => ({
                ...prev,
                videos: prev.videos.map(video =>
                    video._id === videoId
                        ? { ...video, tags: video.tags.filter(tag => tag !== tagToRemove) }
                        : video
                )
            }));
        }

        try {
            await api.removeVideoTag(videoId, tagToRemove);
            trackEvent('admin_tag_remove_success', {
                videoId,
                tag: tagToRemove
            });
            // No need to update UI again since we did it optimistically
        } catch (error) {
            trackEvent('admin_tag_remove_error', {
                videoId,
                tag: tagToRemove,
                error: error.message
            });
            // If there's an error, add the tag back to the UI
            setVideos(prev => prev.map(video => 
                video._id === videoId
                    ? { ...video, tags: [...video.tags, tagToRemove] }
                    : video
            ));
            
            if (selectedUser) {
                setSelectedUser(prev => ({
                    ...prev,
                    videos: prev.videos.map(video =>
                        video._id === videoId
                            ? { ...video, tags: [...video.tags, tagToRemove] }
                            : video
                    )
                }));
            }
            setErrors(['Failed to remove tag: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Handle cut addition
    const handleAddCut = async (e, videoId) => {
        e.preventDefault();
        if (!newCut.name.trim() || !newCut.url.trim()) {
            setErrors(['Please provide both name and URL for the cut']);
            return;
        }

        try {
            const response = await api.addVideoCut(videoId, newCut);
            
            // Update local state
            setVideos(prev => prev.map(video => 
                video._id === videoId
                    ? { ...video, cuts: [...video.cuts, response.data] }
                    : video
            ));
            
            if (selectedUser) {
                setSelectedUser(prev => ({
                    ...prev,
                    videos: prev.videos.map(video =>
                        video._id === videoId
                            ? { ...video, cuts: [...video.cuts, response.data] }
                            : video
                    )
                }));
            }

            setSelectedVideo(null);
            setNewCut({ name: '', url: '' });
        } catch (error) {
            setErrors(['Failed to add cut: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Handle cut removal
    const handleRemoveCut = async (videoId, cutId) => {
        try {
            await api.removeVideoCut(videoId, cutId);
            
            // Update local state
            setVideos(prev => prev.map(video => 
                video._id === videoId
                    ? { ...video, cuts: video.cuts.filter(cut => cut._id !== cutId) }
                    : video
            ));
            
            if (selectedUser) {
                setSelectedUser(prev => ({
                    ...prev,
                    videos: prev.videos.map(video =>
                        video._id === videoId
                            ? { ...video, cuts: video.cuts.filter(cut => cut._id !== cutId) }
                            : video
                    )
                }));
            }
        } catch (error) {
            setErrors(['Failed to remove cut: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Handle cut visibility toggle
    const handleToggleCutVisibility = async (cutId, videoId) => {
        try {
            const response = await api.toggleCutVisibility(cutId);
            const updatedCut = response.data.cut;
            
            // Update local state
            setVideos(prev => prev.map(video => 
                video._id === videoId
                    ? { 
                        ...video, 
                        cuts: video.cuts.map(cut => 
                            cut._id === cutId ? { ...cut, hidden: updatedCut.hidden } : cut
                        )
                    }
                    : video
            ));
            
            if (selectedUser) {
                setSelectedUser(prev => ({
                    ...prev,
                    videos: prev.videos.map(video =>
                        video._id === videoId
                            ? { 
                                ...video, 
                                cuts: video.cuts.map(cut => 
                                    cut._id === cutId ? { ...cut, hidden: updatedCut.hidden } : cut
                                )
                            }
                            : video
                    )
                }));
            }
            
            trackEvent('admin_cut_visibility_toggled', {
                cutId,
                videoId,
                hidden: updatedCut.hidden
            });
        } catch (error) {
            setErrors(['Failed to toggle cut visibility: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Handle generated content upload
    const handleGeneratedContentUpload = async (userId, file) => {
        try {
            const formData = new FormData();
            formData.append('content', file);
            
            await api.uploadGeneratedContent(userId, formData);
            
            // Refresh user data
            fetchUsers();
            setErrors(['Content uploaded successfully']);
        } catch (error) {
            setErrors(['Failed to upload content: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Authentication
    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            // TODO: Replace with proper admin authentication
            if (password === 'a') {
                setIsAuthenticated(true);
                setErrors([]);
                localStorage.setItem('adminToken', 'dummy-token'); // Replace with real token
            } else {
                setErrors(['Invalid password']);
            }
        } catch (error) {
            setErrors(['Authentication failed: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Handle video download
    const handleDownloadVideo = async (video) => {
        try {
            // Fetch the video file
            const response = await fetch(video.url);
            const blob = await response.blob();
            
            // Create a blob URL
            const blobUrl = window.URL.createObjectURL(blob);
            
            // Create a temporary anchor element
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = video.title || 'video.mp4';
            link.style.display = 'none';
            
            // Append to body, click, and cleanup
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Error downloading video:', error);
            setErrors(['Failed to download video: ' + error.message]);
        }
    };

    // Handle generated video download
    const handleDownloadGeneratedVideo = async (video) => {
        try {
            // Get pre-signed download URL from backend
            const response = await api.getVideoDownloadUrl(video._id);
            const downloadUrl = response.data.downloadUrl;
            
            // Create a temporary anchor element for download
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = video.title || 'video.mp4';
            link.style.display = 'none';
            
            // Append to body, click, and cleanup
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading generated video:', error);
            setErrors(['Failed to download generated video: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Handle script-generated video download
    const handleDownloadScriptVideo = async (script) => {
        try {
            // For script videos, we can try direct download since they might have different CORS settings
            // If this fails, we'll need to create a backend endpoint for script videos too
            const response = await fetch(script.videoUrl);
            const blob = await response.blob();
            
            // Create a blob URL
            const blobUrl = window.URL.createObjectURL(blob);
            
            // Create a temporary anchor element
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `script_video_${script._id}.mp4`;
            link.style.display = 'none';
            
            // Append to body, click, and cleanup
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Error downloading script video:', error);
            setErrors(['Failed to download script video: ' + error.message]);
        }
    };

    // Handle video deletion
    const handleDeleteVideo = async (videoId) => {
        if (!window.confirm('Are you sure you want to delete this video?')) {
            trackEvent('admin_video_delete_cancelled', { videoId });
            return;
        }

        trackEvent('admin_video_delete_attempt', { videoId });

        // Optimistically remove the video from the UI
        setVideos(prev => prev.filter(video => video._id !== videoId));
        
        if (selectedUser) {
            setSelectedUser(prev => ({
                ...prev,
                videos: prev.videos.filter(video => video._id !== videoId)
            }));
        }

        try {
            await api.adminDeleteVideo(videoId);
            trackEvent('admin_video_delete_success', { videoId });
            // No need to update UI again since we did it optimistically
        } catch (error) {
            trackEvent('admin_video_delete_error', {
                videoId,
                error: error.message
            });
            console.error('Error deleting video:', error);
            // If there's an error, add the video back to the list
            const videoToRestore = videos.find(v => v._id === videoId);
            if (videoToRestore) {
                setVideos(prev => [...prev, videoToRestore]);
                if (selectedUser) {
                    setSelectedUser(prev => ({
                        ...prev,
                        videos: [...prev.videos, videoToRestore]
                    }));
                }
            }
            setErrors(['Failed to delete video: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Handle file drop for cuts
    const handleCutDrop = async (e) => {
        e.preventDefault();
        setIsDragging(false);
        
        const files = Array.from(e.dataTransfer.files).filter(file => 
            file.type.startsWith('video/')
        );

        if (files.length > 20) {
            setErrors(['You can only upload up to 20 files at once']);
            return;
        }

        for (const file of files) {
            try {
                const formData = new FormData();
                formData.append('cut', file);
                formData.append('name', file.name);

                const response = await api.addVideoCut(selectedVideo._id, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                
                // Update local state
                setVideos(prev => prev.map(video => 
                    video._id === selectedVideo._id
                        ? { ...video, cuts: [...(video.cuts || []), response.data] }
                        : video
                ));
                
                if (selectedUser) {
                    setSelectedUser(prev => ({
                        ...prev,
                        videos: prev.videos.map(video =>
                            video._id === selectedVideo._id
                                ? { ...video, cuts: [...(video.cuts || []), response.data] }
                                : video
                        )
                    }));
                }
            } catch (error) {
                setErrors(['Failed to add cut: ' + (error.response?.data?.error || error.message)]);
            }
        }

        setSelectedVideo(null);
    };

    // Add handleFileSelect function
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        handleCutDrop({ preventDefault: () => {}, dataTransfer: { files } });
    };

    // Admin video upload handlers
    const handleVideoDrop = (e) => {
        e.preventDefault();
        setIsDraggingVideos(false);
        
        if (!selectedUser) {
            setErrors(prev => [...prev, 'Please select a user first']);
            return;
        }
        
        const files = Array.from(e.dataTransfer.files).filter(file => 
            file.type.startsWith('video/') || file.type.startsWith('image/')
        );

        if (files.length > 50) {
            setErrors(prev => [...prev, 'You can only upload up to 50 files at once']);
            return;
        }

        handleVideoFiles(files);
    };

    const handleVideoFileSelect = (e) => {
        if (!selectedUser) {
            setErrors(prev => [...prev, 'Please select a user first']);
            return;
        }
        
        const files = Array.from(e.target.files);
        handleVideoFiles(files);
    };

    const handleVideoFiles = async (files) => {
        const uploadErrors = [];

        // Initialize upload stats
        setVideoUploadStats({
            totalFiles: files.length,
            currentFileIndex: 0,
            completedFiles: 0,
            failedFiles: 0,
            currentFileName: files[0]?.name || ''
        });

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            
            // Update current file being processed
            setVideoUploadStats(prev => ({
                ...prev,
                currentFileIndex: i + 1,
                currentFileName: file.name
            }));

            // Check file size
            if (file.size > 500 * 1024 * 1024) { // 500MB
                uploadErrors.push(`File ${file.name} is too large. Maximum size is 500MB`);
                setVideoUploadStats(prev => ({
                    ...prev,
                    failedFiles: prev.failedFiles + 1
                }));
                continue;
            }

            try {
                // Create FormData
                const formData = new FormData();
                formData.append('video', file);

                const response = await api.adminUploadVideo(selectedUser._id, formData, {
                    onUploadProgress: (progressEvent) => {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setVideoUploadProgress(prev => ({
                            ...prev,
                            [file.name]: progress
                        }));
                    }
                });

                // Update selected user's videos
                setSelectedUser(prev => ({
                    ...prev,
                    videos: [...(prev.videos || []), response.data.video]
                }));

                // Clear progress after successful upload
                setVideoUploadProgress(prev => {
                    const newProgress = { ...prev };
                    delete newProgress[file.name];
                    return newProgress;
                });

                // Update completed files count
                setVideoUploadStats(prev => ({
                    ...prev,
                    completedFiles: prev.completedFiles + 1
                }));
            } catch (error) {
                console.error('Upload error:', error);
                uploadErrors.push(`Failed to upload ${file.name}: ${error.response?.data?.error || error.message}`);
                
                // Clear progress for failed upload
                setVideoUploadProgress(prev => {
                    const newProgress = { ...prev };
                    delete newProgress[file.name];
                    return newProgress;
                });

                // Update failed files count
                setVideoUploadStats(prev => ({
                    ...prev,
                    failedFiles: prev.failedFiles + 1
                }));
            }
        }

        // Clear upload stats after all files are processed
        setTimeout(() => {
            setVideoUploadStats({
                totalFiles: 0,
                currentFileIndex: 0,
                completedFiles: 0,
                failedFiles: 0,
                currentFileName: ''
            });
        }, 2000);

        // Add any errors
        if (uploadErrors.length > 0) {
            setErrors(prev => [...prev, ...uploadErrors]);
        }
    };

    // Handle generated video upload
    const handleGeneratedVideoDrop = async (e) => {
        e.preventDefault();
        setIsDraggingGenerated(false);
        
        const files = Array.from(e.dataTransfer.files).filter(file => 
            file.type.startsWith('video/')
        );

        trackEvent('admin_generated_video_upload_attempt', {
            count: files.length,
            userId: selectedUser?._id
        });

        if (files.length > 20) {
            trackEvent('admin_generated_video_upload_error', {
                error: 'Too many files',
                count: files.length
            });
            setErrors(['You can only upload up to 20 files at once']);
            return;
        }

        for (const file of files) {
            try {
                const formData = new FormData();
                formData.append('video', file);
                formData.append('title', file.name);

                const response = await api.uploadGeneratedVideo(selectedUser._id, formData);
                trackEvent('admin_generated_video_upload_success', {
                    fileName: file.name,
                    fileSize: file.size,
                    userId: selectedUser._id
                });
                const newVideo = response.data;
                setGeneratedVideos(prev => {
                    const updated = [...prev, newVideo];
                    // Update cache
                    setGeneratedVideosCache(prevCache => ({
                        ...prevCache,
                        [selectedUser._id]: updated
                    }));
                    return updated;
                });
            } catch (error) {
                trackEvent('admin_generated_video_upload_error', {
                    fileName: file.name,
                    error: error.message,
                    userId: selectedUser._id
                });
                setErrors(['Failed to upload generated video: ' + (error.response?.data?.error || error.message)]);
            }
        }
    };

    // Handle generated video deletion
    const handleDeleteGeneratedVideo = async (videoId) => {
        if (!window.confirm('Are you sure you want to delete this generated video?')) {
            trackEvent('admin_generated_video_delete_cancelled', { videoId });
            return;
        }

        trackEvent('admin_generated_video_delete_attempt', { videoId });

        try {
            await api.deleteGeneratedVideo(videoId);
            trackEvent('admin_generated_video_delete_success', { videoId });
            setGeneratedVideos(prev => {
                const updated = prev.filter(video => video._id !== videoId);
                // Update cache
                setGeneratedVideosCache(prevCache => ({
                    ...prevCache,
                    [selectedUser._id]: updated
                }));
                return updated;
            });
        } catch (error) {
            trackEvent('admin_generated_video_delete_error', {
                videoId,
                error: error.message
            });
            setErrors(['Failed to delete generated video: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Handle tab change
    const handleTabChange = (tab) => {
        trackEvent('admin_tab_changed', {
            from: activeTab,
            to: tab
        });
        setActiveTab(tab);
    };

    // Handle user content tab change
    const handleUserContentTabChange = (tab) => {
        trackEvent('admin_user_content_tab_changed', {
            from: userContentTab,
            to: tab,
            userId: selectedUser?._id
        });
        setUserContentTab(tab);
        
        // Fetch data for the new tab if we have a selected user
        if (selectedUser) {
            if (tab === 'generated') {
                fetchGeneratedVideos(selectedUser._id); // Uses cache automatically
            } else if (tab === 'studio') {
                fetchUserScripts(selectedUser._id); // Uses cache automatically
            }
        }
    };

    // Add function to fetch user scripts (with caching)
    const fetchUserScripts = async (userId, forceRefresh = false) => {
        // Check cache first
        if (!forceRefresh && scriptsCache[userId]) {
            setGeneratedScripts(scriptsCache[userId]);
            return;
        }

        try {
            const response = await api.getUserScripts(userId);
            const scripts = response.data;
            
            // Update cache and state
            setScriptsCache(prev => ({
                ...prev,
                [userId]: scripts
            }));
            setGeneratedScripts(scripts);
            console.log('Loaded scripts for user:', scripts.length);
        } catch (error) {
            console.error('Error fetching user scripts:', error);
            setErrors(['Failed to fetch scripts: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Handle user selection
    const handleUserSelect = (user) => {
        trackEvent('admin_user_selected', {
            userId: user._id,
            userName: user.name
        });
        setSelectedUser(user);
        
        // Fetch data for the current tab when user changes
        if (userContentTab === 'generated') {
            fetchGeneratedVideos(user._id);
        } else if (userContentTab === 'studio') {
            fetchUserScripts(user._id);
        }
        // Videos and profile tabs use data from selectedUser, so no fetch needed
    };

    // Add this new function to handle user profile updates
    const handleUserProfileUpdate = async (userId) => {
        trackEvent('admin_user_profile_update_attempt', { userId });

        try {
            await api.updateUserProfile(userId, userEditForm);
            trackEvent('admin_user_profile_update_success', { userId });
            
            // Update local state
            const updatedUser = {
                currentSituation: userEditForm.currentSituation,
                shortTermGoals: userEditForm.shortTermGoals,
                longTermGoals: userEditForm.longTermGoals,
                websiteUrl: userEditForm.websiteUrl,
                customFontUrl: userEditForm.customFontUrl,
                contentStrategy: userEditForm.contentStrategy
            };
            
            const updatedProspectingProfile = {
                currentRole: userEditForm.currentRole,
                companyPurpose: userEditForm.companyPurpose,
                targetAudience: userEditForm.targetAudience,
                howToBePerceived: userEditForm.howToBePerceived,
                currentMarketingStrategy: userEditForm.currentMarketingStrategy,
                contentIdeas: userEditForm.contentIdeas,
                instagramUrl: userEditForm.instagramUrl,
                inspiringBrands: userEditForm.inspiringBrands,
                competitors: userEditForm.competitors,
                targetChallenge: userEditForm.targetChallenge
            };
            
            setUsers(prev => prev.map(user => 
                user._id === userId 
                    ? { 
                        ...user, 
                        profile: updatedUser,
                        prospectingProfile: updatedProspectingProfile,
                        additionalInformation: userEditForm.additionalInformation,
                        socialMedia: userEditForm.socialMedia,
                        videoProduction: userEditForm.videoProduction
                    }
                    : user
            ));
            
            // Update selectedUser state to reflect changes immediately
            setSelectedUser(prev => prev && prev._id === userId ? {
                ...prev,
                profile: updatedUser,
                prospectingProfile: updatedProspectingProfile,
                additionalInformation: userEditForm.additionalInformation,
                socialMedia: userEditForm.socialMedia,
                videoProduction: userEditForm.videoProduction
            } : prev);
            

            
            setEditingUser(null);
            setErrors([]);
        } catch (error) {
            trackEvent('admin_user_profile_update_error', {
                userId,
                error: error.message
            });
            setErrors(['Failed to update user profile: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Add this function to handle edit form changes
    const handleEditFormChange = (field, value) => {
        setUserEditForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // User filtering functions
    const applyUserFilters = (userList) => {
        let filtered = userList;
        
        // Filter by selected user IDs
        if (selectedUserIds.length > 0) {
            filtered = filtered.filter(user => selectedUserIds.includes(user._id));
        }
        
        // Filter by search query
        if (userSearchQuery.trim()) {
            const query = userSearchQuery.toLowerCase();
            filtered = filtered.filter(user => 
                user.name.toLowerCase().includes(query) ||
                user.email?.toLowerCase().includes(query)
            );
        }
        
        return filtered;
    };

    const handleUserToggle = (userId) => {
        const newSelectedIds = selectedUserIds.includes(userId)
            ? selectedUserIds.filter(id => id !== userId)
            : [...selectedUserIds, userId];
        
        setSelectedUserIds(newSelectedIds);
        localStorage.setItem('backoffice_selected_users', JSON.stringify(newSelectedIds));
        
        // Apply filters immediately
        const filtered = applyUserFilters(users);
        setFilteredUsers(filtered);
    };

    const clearAllFilters = () => {
        setSelectedUserIds([]);
        setUserSearchQuery('');
        localStorage.setItem('backoffice_selected_users', JSON.stringify([]));
        setFilteredUsers(users);
    };

    const handleSearchChange = (query) => {
        setUserSearchQuery(query);
        
        // Apply filters immediately
        const filtered = applyUserFilters(users);
        setFilteredUsers(filtered);
    };

    // Add function to handle waiting messages display
    const handleWaitingMessages = () => {
        let messageIndex = 0;
        const messageInterval = 2000; // 2 seconds per message

        const showNextMessage = () => {
            if (messageIndex < WAITING_MESSAGES.length) {
                setCurrentWaitingMessage(messageIndex);
                messageIndex++;
                setTimeout(showNextMessage, messageInterval);
            }
        };

        showNextMessage();
    };



    // Modify the handleGenerateScripts function for async generation
    const handleGenerateScripts = async () => {
        if (!selectedUser) return;
        
        setIsGeneratingScripts(true);
        setCurrentWaitingMessage(0);
        
        try {
            // Start displaying waiting messages
            handleWaitingMessages();

            // Start async generation
            const response = await api.generateVideoScriptsAsync({
                userId: selectedUser._id,
                hooks: scriptHooks,
                includePublishedContext: includePublishedContext,
                creativityLevel: creativityLevel
            });

            console.log('Async generation started:', response.data);

            // Start polling for status
            const pollInterval = setInterval(async () => {
                try {
                    const statusResponse = await api.checkScriptsStatus(selectedUser._id);
                    const status = statusResponse.data;
                    
                    console.log('Generation status:', status);
                    
                    if (status.status === 'completed') {
                        clearInterval(pollInterval);
                        
                        // Append new scripts to existing ones immediately
                        const newScripts = [...generatedScripts, ...status.scripts];
                        setGeneratedScripts(newScripts);
                        
                        // Update cache
                        setScriptsCache(prev => ({
                            ...prev,
                            [selectedUser._id]: newScripts
                        }));
                        
                        setIsGeneratingScripts(false);
                        
                        trackEvent('admin_scripts_generated_async', {
                            userId: selectedUser._id,
                            count: status.scripts.length,
                            includePublishedContext: includePublishedContext,
                            creativityLevel: creativityLevel
                        });
                        
                    } else if (status.status === 'error') {
                        clearInterval(pollInterval);
                        setIsGeneratingScripts(false);
                        setErrors(['Failed to generate scripts: ' + (status.error || 'Unknown error')]);
                        
                        trackEvent('admin_scripts_generation_error_async', {
                            userId: selectedUser._id,
                            error: status.error
                        });
                        
                    } else if (status.status === 'generating') {
                        // Update progress if available
                        console.log(`Generation progress: ${status.progress}% (${status.currentScript}/16)`);
                        
                        // Update the waiting message based on progress
                        if (status.currentScript) {
                            setCurrentWaitingMessage(Math.min(4, Math.floor((status.currentScript / 16) * 5)));
                        }
                    }
                    
                } catch (pollError) {
                    console.error('Error polling generation status:', pollError);
                }
            }, 2000); // Poll every 2 seconds
            
            // Set a timeout to stop polling after 10 minutes
            setTimeout(() => {
                clearInterval(pollInterval);
                if (isGeneratingScripts) {
                    setIsGeneratingScripts(false);
                    setErrors(['Script generation timed out. Please try again.']);
                }
            }, 10 * 60 * 1000); // 10 minutes timeout
            
        } catch (error) {
            setErrors(['Failed to start script generation: ' + (error.response?.data?.error || error.message)]);
            trackEvent('admin_scripts_generation_start_error', {
                userId: selectedUser._id,
                error: error.message
            });
            setIsGeneratingScripts(false);
        }
    };

    // Add function to shuffle hooks
    const handleShuffleHooks = () => {
        setScriptHooks(getRandomHooks());
        trackEvent('admin_hooks_shuffled', {
            userId: selectedUser?._id
        });
    };

    // Add this function to handle script regeneration
    const handleRegenerateScript = async (index) => {
        if (!selectedUser) return;
        
        const script = generatedScripts[index];
        const customPrompt = script.regenerationPrompt || '';
        
        try {
            const response = await api.regenerateScript(script._id, customPrompt);
            const newScripts = [...generatedScripts];
            newScripts[index] = response.data;
            setGeneratedScripts(newScripts);
            
            // Update cache
            setScriptsCache(prev => ({
                ...prev,
                [selectedUser._id]: newScripts
            }));
            
            trackEvent('admin_script_regenerated', {
                userId: selectedUser._id,
                scriptId: script._id,
                hasCustomPrompt: !!customPrompt
            });
        } catch (error) {
            setErrors(['Failed to regenerate script: ' + (error.response?.data?.error || error.message)]);
            trackEvent('admin_script_regeneration_error', {
                userId: selectedUser._id,
                scriptId: script._id,
                error: error.message
            });
        }
    };

    // Add this function to handle description regeneration
    const handleRegenerateDescription = async (index) => {
        if (!selectedUser) return;
        
        const script = generatedScripts[index];
        if (script.type !== 'text') return;
        
        const customPrompt = script.regenerationPrompt || '';
        
        try {
            const response = await api.regenerateDescription(script._id, customPrompt);
            const newScripts = [...generatedScripts];
            newScripts[index] = {
                ...newScripts[index],
                description: response.data.description
            };
            setGeneratedScripts(newScripts);
            
            trackEvent('admin_description_regenerated', {
                userId: selectedUser._id,
                scriptId: script._id,
                hasCustomPrompt: !!customPrompt
            });
        } catch (error) {
            setErrors(['Failed to regenerate description: ' + (error.response?.data?.error || error.message)]);
            trackEvent('admin_description_regeneration_error', {
                userId: selectedUser._id,
                scriptId: script._id,
                error: error.message
            });
        }
    };

    // Update the handleGenerateVoiceover function
    const handleGenerateVoiceover = async (index) => {
        if (!selectedUser) return;
        
        const script = generatedScripts[index];
        const scriptId = script._id;
        setGeneratingVoiceovers(prev => new Set([...prev, scriptId]));
        
        try {
            // Force save any pending changes before generating voiceover
            if (debouncedSave.current) {
                await debouncedSave.current.flush();
            }
            
            // Prepare voice settings from slider values
            const voiceSettings = {
                style: getVoiceSettingValue('style', voiceStyle),
                stability: getVoiceSettingValue('stability', voiceStability),
                similarity_boost: getVoiceSettingValue('similarity', voiceSimilarity),
                use_speaker_boost: true
            };

            // Determine which voice to use - user's voice or selected default
            const voiceToUse = selectedUser.voiceId || selectedDefaultVoice;

            // Pass the current voice settings and voice ID to the API
            const response = await api.generateVoiceover(scriptId, voiceSettings, voiceToUse);
            const newScripts = [...generatedScripts];
            newScripts[index] = {
                ...newScripts[index],
                voiceoverUrl: response.data.voiceoverUrl
            };
            setGeneratedScripts(newScripts);
            
            trackEvent('admin_voiceover_generated', {
                userId: selectedUser._id,
                scriptId: scriptId
            });
        } catch (error) {
            setErrors(['Failed to generate voiceover: ' + (error.response?.data?.error || error.message)]);
            trackEvent('admin_voiceover_generation_error', {
                userId: selectedUser._id,
                scriptId: scriptId,
                error: error.message
            });
        } finally {
            setGeneratingVoiceovers(prev => {
                const next = new Set(prev);
                next.delete(scriptId);
                return next;
            });
        }
    };

    // Update the handleGenerateVideo function
    const handleGenerateVideo = async (index) => {
        if (!selectedUser) return;
        
        const scriptId = generatedScripts[index]._id;
        const script = generatedScripts[index];
        setGeneratingVideos(prev => new Set([...prev, scriptId]));
        
        // Clear the existing video immediately when starting generation
        const initialScripts = [...generatedScripts];
        initialScripts[index] = {
            ...initialScripts[index],
            videoUrl: null, // Clear the old video
            status: 'processing',
            progress: 0
        };
        setGeneratedScripts(initialScripts);
        
        let fakeProgressInterval = null; // For fake progress during cropping
        
        try {
            // For text-type scripts, check if a specific cut is selected
            let selectedCutId = null;
            if (script.type === 'text') {
                selectedCutId = selectedCutForScript[scriptId];
            }
            
            // Start the video generation first
            const response = await api.generateVideo({
                userId: selectedUser._id,
                scriptId: scriptId,
                autoCutEnabled: autoCutEnabled,
                selectedCutId: selectedCutId, // Pass the selected cut ID
                selectedMusicId: selectedMusicForScript[scriptId], // Pass the selected music ID
                customFontUrl: selectedUser.profile?.customFontUrl || null // Pass custom font URL if available
            });

            // Update with response data
            setGeneratedScripts(currentScripts => {
                const newScripts = [...currentScripts];
                newScripts[index] = {
                    ...newScripts[index],
                    status: response.data.status || 'processing',
                    progress: response.data.progress || 0,
                    videoUrl: response.data.videoUrl || null
                };
                return newScripts;
            });
            
            // Start polling for status updates with timeout
            const startTime = Date.now();
            const TIMEOUT_MINUTES = 5; // 10 minutes timeout
            const TIMEOUT_MS = TIMEOUT_MINUTES * 60 * 1000;
            
            const pollInterval = setInterval(async () => {
                try {
                    // Check if we've exceeded the timeout
                    if (Date.now() - startTime > TIMEOUT_MS) {
                        console.warn(`Video generation timeout after ${TIMEOUT_MINUTES} minutes for script:`, scriptId);
                        clearInterval(pollInterval);
                        if (fakeProgressInterval) {
                            clearInterval(fakeProgressInterval);
                            fakeProgressInterval = null;
                        }
                        setGeneratedScripts(currentScripts => {
                            const newScripts = [...currentScripts];
                            newScripts[index] = {
                                ...newScripts[index],
                                status: 'timeout',
                                progress: 0
                            };
                            return newScripts;
                        });
                        setGeneratingVideos(prev => {
                            const next = new Set(prev);
                            next.delete(scriptId);
                            return next;
                        });
                        setErrors([`Video generation timed out after ${TIMEOUT_MINUTES} minutes. Please try again.`]);
                        return;
                    }
                    
                    const statusResponse = await api.checkVideoStatus(scriptId);
                    const { status, progress, videoUrl } = statusResponse.data;
                    
                    // Handle fake progress for cropping phase (70% to 85%)
                    if (progress >= 70 && progress < 85 && !fakeProgressInterval) {
                        // Start fake progress from current progress
                        let fakeProgress = progress;
                        fakeProgressInterval = setInterval(() => {
                            // Only increment if we haven't reached real 85% yet
                            if (fakeProgress < 80) {
                                fakeProgress += 1;
                                setGeneratedScripts(currentScripts => {
                                    const newScripts = [...currentScripts];
                                    newScripts[index] = {
                                        ...newScripts[index],
                                        progress: fakeProgress
                                    };
                                    return newScripts;
                                });
                            }
                        }, 2000); // 1% every 2 seconds
                    }
                    
                    // Stop fake progress when real progress reaches 85% or higher
                    if (progress >= 85 && fakeProgressInterval) {
                        clearInterval(fakeProgressInterval);
                        fakeProgressInterval = null;
                    }
                    
                    // Update the script with new status and progress
                    // Use functional update to avoid stale closure issues
                    setGeneratedScripts(currentScripts => {
                        const newScripts = [...currentScripts];
                        newScripts[index] = {
                            ...newScripts[index],
                            status,
                            // Use real progress if >= 85%, otherwise keep current progress (which might be fake)
                            progress: progress >= 85 ? progress : (newScripts[index].progress || 0),
                            // Only set videoUrl when generation is complete (status is 'draft' and we have a videoUrl)
                            videoUrl: (status === 'draft' && videoUrl) ? videoUrl : newScripts[index].videoUrl
                        };
                        return newScripts;
                    });

                    // If video is complete (has videoUrl and status is 'draft') or error, stop polling
                    if ((status === 'draft' && videoUrl) || status === 'error') {
                        clearInterval(pollInterval);
                        if (fakeProgressInterval) {
                            clearInterval(fakeProgressInterval);
                            fakeProgressInterval = null;
                        }
                        setGeneratingVideos(prev => {
                            const next = new Set(prev);
                            next.delete(scriptId);
                            return next;
                        });
                    }
                } catch (error) {
                    console.error('Error checking video status:', error);
                    clearInterval(pollInterval);
                    if (fakeProgressInterval) {
                        clearInterval(fakeProgressInterval);
                        fakeProgressInterval = null;
                    }
                    setGeneratingVideos(prev => {
                        const next = new Set(prev);
                        next.delete(scriptId);
                        return next;
                    });
                }
            }, 2000); // Poll every 2 seconds
            
            trackEvent('admin_video_generated', {
                userId: selectedUser._id,
                scriptId: scriptId
            });
        } catch (error) {
            setErrors(['Failed to generate video: ' + (error.response?.data?.error || error.message)]);
            trackEvent('admin_video_generation_error', {
                userId: selectedUser._id,
                scriptId: scriptId,
                error: error.message
            });
            // Clean up fake progress interval on error
            if (fakeProgressInterval) {
                clearInterval(fakeProgressInterval);
            }
            // Only remove from generating state if there's an error starting the process
            setGeneratingVideos(prev => {
                const next = new Set(prev);
                next.delete(scriptId);
                return next;
            });
        }
    };

    // Add this function to handle video publishing
    const handlePublishVideo = async (index) => {
        if (!selectedUser) return;
        
        try {
            await api.publishVideo(generatedScripts[index]._id);
            const newScripts = [...generatedScripts];
            newScripts[index] = {
                ...newScripts[index],
                status: 'published'
            };
            setGeneratedScripts(newScripts);
            
            // Refresh the generated videos list to show the newly published video
            if (userContentTab === 'generated') {
                fetchGeneratedVideos(selectedUser._id);
            }
            
            trackEvent('admin_video_published', {
                userId: selectedUser._id,
                scriptId: generatedScripts[index]._id
            });
        } catch (error) {
            setErrors(['Failed to publish video: ' + (error.response?.data?.error || error.message)]);
            trackEvent('admin_video_publish_error', {
                userId: selectedUser._id,
                scriptId: generatedScripts[index]._id,
                error: error.message
            });
        }
    };

    // Add this function to handle script deletion
    const handleDeleteScript = async (scriptId) => {
        if (!selectedUser) return;
        
        try {
            await api.deleteScript(scriptId);
            setGeneratedScripts(prev => {
                const updated = prev.filter(script => script._id !== scriptId);
                // Update cache
                setScriptsCache(prevCache => ({
                    ...prevCache,
                    [selectedUser._id]: updated
                }));
                return updated;
            });
            
            trackEvent('admin_script_deleted', {
                userId: selectedUser._id,
                scriptId: scriptId
            });
        } catch (error) {
            setErrors(['Failed to delete script: ' + (error.response?.data?.error || error.message)]);
            trackEvent('admin_script_delete_error', {
                userId: selectedUser._id,
                scriptId: scriptId,
                error: error.message
            });
        }
    };

    // Add this function to handle clearing all scripts
    const handleClearAllScripts = async () => {
        if (!selectedUser || !window.confirm('Are you sure you want to delete all scripts?')) return;
        
        try {
            await api.deleteAllScripts(selectedUser._id);
            setGeneratedScripts([]);
            
            // Update cache
            setScriptsCache(prev => ({
                ...prev,
                [selectedUser._id]: []
            }));
            
            trackEvent('admin_all_scripts_deleted', {
                userId: selectedUser._id
            });
        } catch (error) {
            setErrors(['Failed to clear scripts: ' + (error.response?.data?.error || error.message)]);
            trackEvent('admin_clear_scripts_error', {
                userId: selectedUser._id,
                error: error.message
            });
        }
    };

    // Add function to handle generating all voiceovers
    const handleGenerateAllVoiceovers = async () => {
        if (!selectedUser) return;
        
        // Find all voiceover-type scripts that don't have voiceovers yet
        const scriptsNeedingVoiceover = generatedScripts.filter(script => 
            script.type === 'voiceover' && !script.voiceoverUrl
        );
        
        if (scriptsNeedingVoiceover.length === 0) {
            setErrors(['No voiceover scripts found that need voiceover generation']);
            return;
        }

        const confirmMessage = `Generate voiceovers for ${scriptsNeedingVoiceover.length} script${scriptsNeedingVoiceover.length > 1 ? 's' : ''}?`;
        if (!window.confirm(confirmMessage)) return;
        
        trackEvent('admin_bulk_voiceover_generation_started', {
            userId: selectedUser._id,
            scriptCount: scriptsNeedingVoiceover.length
        });

        // Process each script that needs a voiceover
        for (const script of scriptsNeedingVoiceover) {
            const index = generatedScripts.findIndex(s => s._id === script._id);
            if (index !== -1) {
                try {
                    // Add a small delay between requests to avoid overwhelming the API
                    if (scriptsNeedingVoiceover.indexOf(script) > 0) {
                        await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay
                    }
                    await handleGenerateVoiceover(index);
                } catch (error) {
                    console.error(`Failed to generate voiceover for script ${script._id}:`, error);
                    // Continue with other scripts even if one fails
                }
            }
        }
        
        trackEvent('admin_bulk_voiceover_generation_completed', {
            userId: selectedUser._id,
            scriptCount: scriptsNeedingVoiceover.length
        });
    };

    // Add function to handle Auto-Cut toggle
    const handleAutoCutToggle = (enabled) => {
        setAutoCutEnabled(enabled);
        localStorage.setItem('admin-auto-cut-enabled', JSON.stringify(enabled));
        trackEvent('admin_auto_cut_toggled', {
            userId: selectedUser?._id,
            enabled
        });
    };

    // Add function to handle generating all videos
    const handleGenerateAllVideos = async () => {
        if (!selectedUser) return;
        
        // Find all scripts that can generate videos but don't have completed videos yet
        const scriptsNeedingVideo = generatedScripts.filter(script => 
            (script.voiceoverUrl || script.type === 'text') && !(script.videoUrl && script.status === 'draft' && script.progress === 100)
        );
        
        if (scriptsNeedingVideo.length === 0) {
            setErrors(['No scripts found that need video generation. Voiceover scripts must have voiceovers generated first.']);
            return;
        }

        const confirmMessage = `Generate videos for ${scriptsNeedingVideo.length} script${scriptsNeedingVideo.length > 1 ? 's' : ''}?`;
        if (!window.confirm(confirmMessage)) return;
        
        trackEvent('admin_bulk_video_generation_started', {
            userId: selectedUser._id,
            scriptCount: scriptsNeedingVideo.length
        });

        // Process each script that needs a video
        for (const script of scriptsNeedingVideo) {
            const index = generatedScripts.findIndex(s => s._id === script._id);
            if (index !== -1) {
                try {
                    // Add a small delay between requests to avoid overwhelming the system
                    if (scriptsNeedingVideo.indexOf(script) > 0) {
                        await new Promise(resolve => setTimeout(resolve, 2000)); // 2 second delay for videos
                    }
                    await handleGenerateVideo(index);
                } catch (error) {
                    console.error(`Failed to generate video for script ${script._id}:`, error);
                    // Continue with other scripts even if one fails
                }
            }
        }
        
        trackEvent('admin_bulk_video_generation_completed', {
            userId: selectedUser._id,
            scriptCount: scriptsNeedingVideo.length
        });
    };

    // Add function to fetch available cuts for the user
    const fetchAvailableCuts = async () => {
        if (!selectedUser) return;
        
        try {
            console.log('Fetching cuts for user:', selectedUser._id);
            const response = await api.getUserCuts(selectedUser._id);
            console.log('Cuts response:', response.data);
            setAvailableCuts(response.data);
        } catch (error) {
            console.error('Error fetching cuts:', error);
            console.error('Error details:', {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                url: error.config?.url
            });
            setErrors(['Failed to fetch cuts: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Add function to handle cut selection modal
    const handleSelectCut = async (scriptIndex) => {
        if (!selectedUser) return;
        
        const script = generatedScripts[scriptIndex];
        if (script.type !== 'text') return;
        
        // Fetch available cuts if not already loaded
        if (availableCuts.length === 0) {
            await fetchAvailableCuts();
        }
        
        setCurrentScriptForCutSelection(scriptIndex);
        setShowCutSelectionModal(true);
    };

    // Add function to confirm cut selection
    const handleConfirmCutSelection = (cutId) => {
        if (currentScriptForCutSelection === null) return;
        
        setSelectedCutForScript(prev => ({
            ...prev,
            [generatedScripts[currentScriptForCutSelection]._id]: cutId
        }));
        setShowCutSelectionModal(false);
        setCurrentScriptForCutSelection(null);
    };

    // Add function to fetch available music from global library
    const fetchAvailableMusic = async () => {
        try {
            console.log('Fetching global music library');
            const response = await api.getGlobalMusic();
            console.log('Music response:', response.data);
            setAvailableMusic(response.data);
        } catch (error) {
            console.error('Error fetching music:', error);
            setErrors(['Failed to fetch music: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Add function to handle music upload
    const handleMusicUpload = async (files) => {
        if (!files || files.length === 0) return;

        if (files.length > 30) {
            setErrors(['You can only upload up to 30 files at once']);
            return;
        }

        setIsUploadingMusic(true);
        setUploadProgress({ current: 0, total: files.length });
        setUploadSuccess(false);
        setUploadSuccessMessage('');

        const formData = new FormData();
        Array.from(files).forEach(file => {
            formData.append('music', file);
        });

        try {
            const response = await api.uploadMusic(formData);
            
            // Show success state in modal
            setUploadSuccess(true);
            setUploadSuccessMessage(`Successfully uploaded ${response.data.uploadedCount} music files to global library`);
            
            // Refresh music list
            await fetchAvailableMusic();
            
            // Close modal after 2 seconds
            setTimeout(() => {
                setShowMusicUploadModal(false);
                setUploadSuccess(false);
                setUploadSuccessMessage('');
                musicFileInputRef.current.value = '';
            }, 2000);
            
        } catch (error) {
            setErrors(['Failed to upload music: ' + (error.response?.data?.error || error.message)]);
        } finally {
            setIsUploadingMusic(false);
            setUploadProgress({ current: 0, total: 0 });
        }
    };

    // Add function to handle music file selection
    const handleMusicFileSelect = (e) => {
        const files = Array.from(e.target.files);
        handleMusicUpload(files);
    };

    // Add function to handle music drag and drop
    const handleMusicDrop = async (e) => {
        e.preventDefault();
        setIsDraggingMusic(false);
        
        const files = Array.from(e.dataTransfer.files).filter(file => 
            file.type.startsWith('audio/')
        );

        if (files.length === 0) {
            setErrors(['No audio files found. Please select MP3, WAV, AAC, or OGG files.']);
            return;
        }

        await handleMusicUpload(files);
    };

    // Add function to handle music selection modal
    const handleSelectMusic = async (scriptIndex) => {
        // Fetch available music if not already loaded
        if (availableMusic.length === 0) {
            await fetchAvailableMusic();
        }
        
        setCurrentScriptForMusicSelection(scriptIndex);
        setShowMusicSelectionModal(true);
    };

    // Add function to confirm music selection
    const handleConfirmMusicSelection = (musicId) => {
        if (currentScriptForMusicSelection === null) return;
        
        setSelectedMusicForScript(prev => ({
            ...prev,
            [generatedScripts[currentScriptForMusicSelection]._id]: musicId
        }));
        setShowMusicSelectionModal(false);
        setCurrentScriptForMusicSelection(null);
    };

    // Add function to handle music deletion
    const handleDeleteMusic = async (musicId) => {
        if (!window.confirm('Are you sure you want to delete this music?')) return;
        
        try {
            await api.deleteMusic(musicId);
            setAvailableMusic(prev => prev.filter(music => music._id !== musicId));
            setErrors(['Music deleted successfully']);
        } catch (error) {
            setErrors(['Failed to delete music: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Add state for cut selection modal
    const [showCutSelectionModal, setShowCutSelectionModal] = useState(false);
    const [selectedCutForScript, setSelectedCutForScript] = useState({});
    const [availableCuts, setAvailableCuts] = useState([]);
    const [currentScriptForCutSelection, setCurrentScriptForCutSelection] = useState(null);

    // Add state for music management
    const [showMusicUploadModal, setShowMusicUploadModal] = useState(false);
    const [showMusicSelectionModal, setShowMusicSelectionModal] = useState(false);
    const [selectedMusicForScript, setSelectedMusicForScript] = useState({});
    const [availableMusic, setAvailableMusic] = useState([]);
    const [currentScriptForMusicSelection, setCurrentScriptForMusicSelection] = useState(null);
    const [isDraggingMusic, setIsDraggingMusic] = useState(false);
    const [isUploadingMusic, setIsUploadingMusic] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [uploadSuccessMessage, setUploadSuccessMessage] = useState('');
    const musicFileInputRef = useRef(null);

    if (!isAuthenticated) {
        return (
            <div className="backoffice-login">
                <form onSubmit={handleLogin}>
                    <h2>Admin Access</h2>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter admin password"
                    />
                    <button type="submit">Login</button>
                    {/* Error Messages */}
                    {errors.length > 0 && (
                        <div className="error-container">
                            {errors.map((error, index) => (
                                <div key={index} style={{alignContent: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'row', alignSelf: 'center', alignItems: 'center', justifyItems: 'center'}}>
                                    <AlertCircle size={20} style={{marginRight: '5px'}}/>
                                    {error}
                                </div>
                            ))}
                        </div>
                    )}
                </form>
            </div>
        );
    }

    // Update the video card JSX in both users and videos tabs
    const renderVideoCard = (video) => (
        <div key={video._id} className="video-card">
            <div className="video-preview">
                <video src={video.url} controls />
                <div className="video-status">
                    <select 
                        value={video.status}
                        onChange={(e) => handleStatusChange(video._id, e.target.value)}
                    >
                        <option value="processing">Processing</option>
                        <option value="processed">Processed</option>
                    </select>
                </div>
            </div>
            <div className="video-info">
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                    alignContent: 'center'
                }}>
                    <h3 style={{margin: '0'}}>{video.title}</h3>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button 
                            className="download-video-btn"
                            onClick={() => handleDownloadVideo(video)}
                            style={{
                                backgroundColor: 'var(--button-bg)',
                                color: 'var(--button-text)',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                opacity: '0.9',
                                transition: 'opacity 0.2s',
                                marginLeft: '10px'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                        >
                            <Download size={14} />
                        </button>
                        <button 
                            className="delete-video-btn"
                            onClick={() => handleDeleteVideo(video._id)}
                            style={{
                                backgroundColor: 'var(--input-bg)',
                                color: 'var(--text-color)',
                                border: '1px solid var(--input-border)',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                opacity: '0.9',
                                transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.opacity = '1';
                                e.currentTarget.style.backgroundColor = '#ff4444';
                                e.currentTarget.style.color = 'white';
                                e.currentTarget.style.borderColor = '#ff4444';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.opacity = '0.9';
                                e.currentTarget.style.backgroundColor = 'var(--input-bg)';
                                e.currentTarget.style.color = 'var(--text-color)';
                                e.currentTarget.style.borderColor = 'var(--input-border)';
                            }}
                        >
                            <Trash size={14} />
                        </button>
                    </div>
                </div>
                
                {video.userName && <p className="video-owner">Owner: {video.userName}</p>}
                
                {/* Tags */}
                <div className="video-tags">
                    {video.tags.map(tag => (
                        <span key={`${video._id}-${tag}`} className="tag">
                            {tag}
                            <button onClick={() => handleRemoveTag(video._id, tag)}>
                                <X size={14} />
                            </button>
                        </span>
                    ))}
                    <div className="add-tag">
                        <input
                            type="text"
                            value={tagInputs[video._id] || ''}
                            onChange={(e) => setTagInputs(prev => ({
                                ...prev,
                                [video._id]: e.target.value
                            }))}
                            placeholder="Add tag..."
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddTag(video._id);
                                }
                            }}
                        />
                        <button onClick={() => handleAddTag(video._id)}>
                            <Plus size={14} />
                        </button>
                    </div>
                </div>

                {/* Description */}
                <div className="video-description">
                    <p>{video.description || 'No description'}</p>
                </div>

                {/* Cuts */}
                <div className="video-cuts">
                    <h4>Cuts</h4>
                    <div className="cuts-list">
                        {(video.cuts || []).map((cut, index) => (
                            <div key={`${video._id}-cut-${index}`} className="cut-item">
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '8px'
                                }}>
                                    <span style={{
                                        opacity: cut.hidden ? '0.5' : '1',
                                        textDecoration: cut.hidden ? 'line-through' : 'none'
                                    }}>
                                        {cut.name} {cut.hidden && '(Hidden)'}
                                    </span>
                                    <button
                                        onClick={() => handleToggleCutVisibility(cut._id, video._id)}
                                        style={{
                                            background: cut.hidden ? '#28a745' : '#dc3545',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '4px 8px',
                                            fontSize: '12px',
                                            cursor: 'pointer'
                                        }}
                                        title={cut.hidden ? 'Show cut' : 'Hide cut'}
                                    >
                                        {cut.hidden ? 'Show' : 'Hide'}
                                    </button>
                                </div>
                                <video 
                                    src={cut.url} 
                                    controls 
                                    style={{
                                        opacity: cut.hidden ? '0.5' : '1'
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                    <button 
                        className="add-cut-button"
                        onClick={() => setSelectedVideo(video)}
                    >
                        <Plus size={14} />
                        Add Cut
                    </button>
                </div>
            </div>
        </div>
    );

    // Update the user content section JSX
    const renderUserContent = () => (
        <div className="user-content">
            <h2>{selectedUser.name}'s Content</h2>
            <div className="content-tabs">
                <button 
                    className={`tab-button ${userContentTab === 'videos' ? 'active' : ''}`}
                    onClick={() => handleUserContentTabChange('videos')}
                >
                    <Video size={16} />
                    Videos
                </button>
                <button 
                    className={`tab-button ${userContentTab === 'generated' ? 'active' : ''}`}
                    onClick={() => handleUserContentTabChange('generated')}
                >
                    <Zap size={16} />
                    Generated Videos
                </button>
                <button 
                    className={`tab-button ${userContentTab === 'profile' ? 'active' : ''}`}
                    onClick={() => handleUserContentTabChange('profile')}
                >
                    <User size={16} />
                    Profile
                </button>
                <button 
                    className={`tab-button ${userContentTab === 'studio' ? 'active' : ''}`}
                    onClick={() => handleUserContentTabChange('studio')}
                >
                    <Video size={16} />
                    Video Studio
                </button>
            </div>

            {userContentTab === 'videos' ? (
                <div className="videos-content">
                    {/* Admin Upload Interface */}
                    <div 
                        className={`upload-area ${isDraggingVideos ? 'dragging' : ''}`}
                        onDragOver={(e) => {
                            e.preventDefault();
                            setIsDraggingVideos(true);
                        }}
                        onDragLeave={() => setIsDraggingVideos(false)}
                        onDrop={handleVideoDrop}
                    >
                        <input
                            ref={videoFileInputRef}
                            type="file"
                            multiple
                            accept="video/*,image/*"
                            onChange={handleVideoFileSelect}
                            style={{ display: 'none' }}
                        />
                        <label 
                            className="upload-label"
                            onClick={() => videoFileInputRef.current?.click()}
                        >
                            <Plus size={24} />
                            <span>Upload videos for {selectedUser?.name || 'selected user'}</span>
                            <span className="upload-hint">Up to 50 files at once (500MB each)</span>
                        </label>
                    </div>

                    {/* Upload Progress */}
                    {Object.keys(videoUploadProgress).length > 0 && (
                        <div className="upload-progress">
                            {/* Upload Summary */}
                            {videoUploadStats.totalFiles > 0 && (
                                <div className="upload-summary" style={{
                                    padding: '12px',
                                    marginBottom: '16px',
                                    backgroundColor: 'var(--input-bg)',
                                    borderRadius: '8px',
                                    border: '1px solid var(--input-border)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '8px'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontWeight: 'bold', color: 'var(--text-color)' }}>
                                            Uploading: {videoUploadStats.currentFileIndex} of {videoUploadStats.totalFiles}
                                        </span>
                                        {videoUploadStats.currentFileName && (
                                            <span style={{ 
                                                color: 'var(--text-secondary)',
                                                fontSize: '14px',
                                                maxWidth: '200px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {videoUploadStats.currentFileName}
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', gap: '16px', fontSize: '14px' }}>
                                        <span style={{ color: '#4CAF50' }}>
                                            ✓ {videoUploadStats.completedFiles} completed
                                        </span>
                                        {videoUploadStats.failedFiles > 0 && (
                                            <span style={{ color: '#f44336' }}>
                                                ✗ {videoUploadStats.failedFiles} failed
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                            
                            {/* Individual File Progress */}
                            {Object.entries(videoUploadProgress).map(([fileName, progress]) => (
                                <div key={`progress-${fileName}`} className="progress-item">
                                    <span>{fileName}</span>
                                    <div className="progress-bar">
                                        <div 
                                            className="progress-fill"
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>
                                    <span>{progress}%</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Videos Grid */}
                    <div className="videos-grid">
                        {selectedUser.videos?.map(video => renderVideoCard(video))}
                    </div>
                </div>
            ) : userContentTab === 'generated' ? (
                <div className="generated-videos">
                    <div 
                        className={`upload-area ${isDraggingGenerated ? 'dragging' : ''}`}
                        onDragOver={(e) => {
                            e.preventDefault();
                            setIsDraggingGenerated(true);
                        }}
                        onDragLeave={() => setIsDraggingGenerated(false)}
                        onDrop={handleGeneratedVideoDrop}
                        onClick={() => generatedFileInputRef.current?.click()}
                    >
                        <div className="upload-label">
                            <Plus size={24} />
                            <span>Drag & drop generated videos here or click to select</span>
                            <span className="upload-hint">Up to 20 files at once</span>
                        </div>
                    </div>
                    <input
                        ref={generatedFileInputRef}
                        type="file"
                        multiple
                        accept="video/*"
                        onChange={(e) => handleGeneratedVideoDrop({ 
                            preventDefault: () => {}, 
                            dataTransfer: { files: e.target.files } 
                        })}
                        style={{ display: 'none' }}
                    />
                    <div className="videos-grid">
                        {generatedVideos.map(video => (
                            <div key={video._id} className="video-card">
                                <div className="video-preview">
                                    <video src={video.url} controls />
                                </div>
                                <div className="video-info">
                                    <h3>{video.title}</h3>
                                    <p>{video.description || 'No description'}</p>
                                    <div className="video-tags">
                                        {video.tags.map(tag => (
                                            <span key={tag} className="tag">{tag}</span>
                                        ))}
                                    </div>
                                    <div className="video-actions">
                                        <button 
                                            className="download-video-btn"
                                            style={{
                                                backgroundColor: 'var(--button-bg)',
                                                color: 'var(--button-text)',
                                                border: 'none',
                                                borderRadius: '4px',
                                                padding: '4px 8px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                opacity: '0.9',
                                                transition: 'opacity 0.2s',
                                            }}
                                            onClick={() => handleDownloadGeneratedVideo(video)}
                                        >
                                            <Download size={14} />
                                        </button>
                                        <button 
                                            className="delete-video-btn"
                                            style={{
                                                backgroundColor: 'var(--input-bg)',
                                                color: 'var(--text-color)',
                                                border: '1px solid var(--input-border)',
                                                borderRadius: '4px',
                                                padding: '4px 8px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                opacity: '0.9',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseOver={(e) => {
                                                e.currentTarget.style.opacity = '1';
                                                e.currentTarget.style.backgroundColor = '#ff4444';
                                                e.currentTarget.style.color = 'white';
                                                e.currentTarget.style.borderColor = '#ff4444';
                                            }}
                                            onMouseOut={(e) => {
                                                e.currentTarget.style.opacity = '0.9';
                                                e.currentTarget.style.backgroundColor = 'var(--input-bg)';
                                                e.currentTarget.style.color = 'var(--text-color)';
                                                e.currentTarget.style.borderColor = 'var(--input-border)';
                                            }}
                                            onClick={() => handleDeleteGeneratedVideo(video._id)}
                                        >
                                            <Trash size={14} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : userContentTab === 'profile' ? (
                <div className="user-profile-section">
                    {editingUser === selectedUser._id ? (
                        <div className="user-profile-edit">
                            <div className="profile-section">
                                <h4>Profile</h4>
                                <TextareaAutosize
                                    placeholder="Current Situation"
                                    value={userEditForm.currentSituation}
                                    onChange={(e) => handleEditFormChange('currentSituation', e.target.value)}
                                    minRows={3}
                                    maxRows={10}
                                    style={{
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        fontFamily: 'inherit',
                                        backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                        color: darkMode ? '#fff' : '#333',
                                        marginBottom: '10px',
                                        resize: 'none'
                                    }}
                                />
                                <TextareaAutosize
                                    placeholder="Short Term Goals"
                                    value={userEditForm.shortTermGoals}
                                    onChange={(e) => handleEditFormChange('shortTermGoals', e.target.value)}
                                    minRows={3}
                                    maxRows={10}
                                    style={{
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        fontFamily: 'inherit',
                                        backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                        color: darkMode ? '#fff' : '#333',
                                        marginBottom: '10px',
                                        resize: 'none'
                                    }}
                                />
                                <TextareaAutosize
                                    placeholder="Long Term Goals"
                                    value={userEditForm.longTermGoals}
                                    onChange={(e) => handleEditFormChange('longTermGoals', e.target.value)}
                                    minRows={3}
                                    maxRows={10}
                                    style={{
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        fontFamily: 'inherit',
                                        backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                        color: darkMode ? '#fff' : '#333',
                                        marginBottom: '10px',
                                        resize: 'none'
                                    }}
                                />
                                <input
                                    type="url"
                                    placeholder="Website URL (https://your-website.com)"
                                    value={userEditForm.websiteUrl}
                                    onChange={(e) => handleEditFormChange('websiteUrl', e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        fontFamily: 'inherit',
                                        backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                        color: darkMode ? '#fff' : '#333',
                                        marginBottom: '10px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                            <div className="prospecting-section">
                                <h4>Business Profile</h4>
                                <TextareaAutosize
                                    placeholder="Current Role"
                                    value={userEditForm.currentRole}
                                    onChange={(e) => handleEditFormChange('currentRole', e.target.value)}
                                    minRows={3}
                                    maxRows={10}
                                    style={{
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        fontFamily: 'inherit',
                                        backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                        color: darkMode ? '#fff' : '#333',
                                        marginBottom: '10px',
                                        resize: 'none'
                                    }}
                                />
                                <TextareaAutosize
                                    placeholder="Company Purpose"
                                    value={userEditForm.companyPurpose}
                                    onChange={(e) => handleEditFormChange('companyPurpose', e.target.value)}
                                    minRows={3}
                                    maxRows={10}
                                    style={{
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        fontFamily: 'inherit',
                                        backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                        color: darkMode ? '#fff' : '#333',
                                        marginBottom: '10px',
                                        resize: 'none'
                                    }}
                                />
                                <TextareaAutosize
                                    placeholder="Target Audience"
                                    value={userEditForm.targetAudience}
                                    onChange={(e) => handleEditFormChange('targetAudience', e.target.value)}
                                    minRows={3}
                                    maxRows={10}
                                    style={{
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        fontFamily: 'inherit',
                                        backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                        color: darkMode ? '#fff' : '#333',
                                        marginBottom: '10px',
                                        resize: 'none'
                                    }}
                                />
                                <TextareaAutosize
                                    placeholder="How do you want to be perceived?"
                                    value={userEditForm.howToBePerceived}
                                    onChange={(e) => handleEditFormChange('howToBePerceived', e.target.value)}
                                    minRows={3}
                                    maxRows={10}
                                    style={{
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        fontFamily: 'inherit',
                                        backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                        color: darkMode ? '#fff' : '#333',
                                        marginBottom: '10px',
                                        resize: 'none'
                                    }}
                                />
                                <TextareaAutosize
                                    placeholder="Current Marketing Strategy"
                                    value={userEditForm.currentMarketingStrategy}
                                    onChange={(e) => handleEditFormChange('currentMarketingStrategy', e.target.value)}
                                    minRows={3}
                                    maxRows={10}
                                    style={{
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        fontFamily: 'inherit',
                                        backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                        color: darkMode ? '#fff' : '#333',
                                        marginBottom: '10px',
                                        resize: 'none'
                                    }}
                                />
                                <TextareaAutosize
                                    placeholder="Content Ideas"
                                    value={userEditForm.contentIdeas}
                                    onChange={(e) => handleEditFormChange('contentIdeas', e.target.value)}
                                    minRows={3}
                                    maxRows={10}
                                    style={{
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        fontFamily: 'inherit',
                                        backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                        color: darkMode ? '#fff' : '#333',
                                        marginBottom: '10px',
                                        resize: 'none'
                                    }}
                                />
                                <input
                                    type="url"
                                    placeholder="Instagram URL"
                                    value={userEditForm.instagramUrl}
                                    onChange={(e) => handleEditFormChange('instagramUrl', e.target.value)}
                                    style={{
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        fontFamily: 'inherit',
                                        backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                        color: darkMode ? '#fff' : '#333',
                                        marginBottom: '10px'
                                    }}
                                />
                                <TextareaAutosize
                                    placeholder="Brands / Accounts that inspire you"
                                    value={userEditForm.inspiringBrands}
                                    onChange={(e) => handleEditFormChange('inspiringBrands', e.target.value)}
                                    minRows={3}
                                    maxRows={10}
                                    style={{
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        fontFamily: 'inherit',
                                        backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                        color: darkMode ? '#fff' : '#333',
                                        marginBottom: '10px',
                                        resize: 'none'
                                    }}
                                />
                                <TextareaAutosize
                                    placeholder="Competitors and main differentiation"
                                    value={userEditForm.competitors}
                                    onChange={(e) => handleEditFormChange('competitors', e.target.value)}
                                    minRows={3}
                                    maxRows={10}
                                    style={{
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        fontFamily: 'inherit',
                                        backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                        color: darkMode ? '#fff' : '#333',
                                        marginBottom: '10px',
                                        resize: 'none'
                                    }}
                                />
                                <TextareaAutosize
                                    placeholder="Your target's main challenge and how you solve them"
                                    value={userEditForm.targetChallenge}
                                    onChange={(e) => handleEditFormChange('targetChallenge', e.target.value)}
                                    minRows={3}
                                    maxRows={10}
                                    style={{
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        fontFamily: 'inherit',
                                        backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                        color: darkMode ? '#fff' : '#333',
                                        marginBottom: '10px',
                                        resize: 'none'
                                    }}
                                />
                            </div>
                            <div className="additional-info-section">
                                <h4>Additional Information</h4>
                                <TextareaAutosize
                                    placeholder="Additional Information"
                                    value={userEditForm.additionalInformation}
                                    onChange={(e) => handleEditFormChange('additionalInformation', e.target.value)}
                                    minRows={3}
                                    maxRows={10}
                                    style={{
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        fontFamily: 'inherit',
                                        backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                        color: darkMode ? '#fff' : '#333',
                                        marginBottom: '10px',
                                        resize: 'none'
                                    }}
                                />
                            </div>
                            <div className="custom-font-section">
                                <h4>Custom Font (Admin Only)</h4>
                                <input
                                    type="url"
                                    placeholder="Custom Font URL (e.g., https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap)"
                                    value={userEditForm.customFontUrl}
                                    onChange={(e) => handleEditFormChange('customFontUrl', e.target.value)}
                                    style={{
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        fontFamily: 'inherit',
                                        backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                        color: darkMode ? '#fff' : '#333',
                                        marginBottom: '10px'
                                    }}
                                />
                                <p style={{
                                    fontSize: '12px',
                                    color: darkMode ? '#aaa' : '#666',
                                    marginTop: '5px',
                                    marginBottom: '10px'
                                }}>
                                    Enter a Google Fonts URL or any other web font CSS URL. This font will be automatically applied to all video generation for this user.
                                </p>
                            </div>
                            <div className="content-strategy-section">
                                <h4>Content Ideas (Admin Only)</h4>
                                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                    <button 
                                        onClick={handleGenerateContentStrategy}
                                        disabled={isGeneratingContentStrategy}
                                        style={{
                                            backgroundColor: 'var(--button-bg)',
                                            color: 'var(--button-text)',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '8px 16px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            opacity: isGeneratingContentStrategy ? '0.6' : '0.9',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseOver={(e) => !isGeneratingContentStrategy && (e.currentTarget.style.opacity = '1')}
                                        onMouseOut={(e) => !isGeneratingContentStrategy && (e.currentTarget.style.opacity = '0.9')}
                                    >
                                        {isGeneratingContentStrategy ? (
                                            <>
                                                <span className="spinner-border" style={{marginRight:'5px'}}/>
                                                Generating...
                                            </>
                                        ) : (
                                            <>
                                                <Zap size={16} />
                                                Generate Ideas
                                            </>
                                        )}
                                    </button>
                                </div>
                                <TextareaAutosize
                                    placeholder="Content ideas will be generated based on user profile information. These ideas will be included in video generation prompts."
                                    value={userEditForm.contentStrategy}
                                    onChange={(e) => handleEditFormChange('contentStrategy', e.target.value)}
                                    minRows={6}
                                    maxRows={15}
                                    style={{
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        fontFamily: 'inherit',
                                        backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                        color: darkMode ? '#fff' : '#333',
                                        marginBottom: '10px',
                                        resize: 'none'
                                    }}
                                />
                                <p style={{
                                    fontSize: '12px',
                                    color: darkMode ? '#aaa' : '#666',
                                    marginTop: '5px',
                                    marginBottom: '10px'
                                }}>
                                    These content ideas will be automatically included in video generation prompts to provide specific content direction and inspiration.
                                </p>
                            </div>
                            <div className="social-media-section">
                                <h4>Social Media (Admin Only)</h4>
                                
                                {/* Main Platform Selection */}
                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ 
                                        display: 'block', 
                                        marginBottom: '10px', 
                                        fontSize: '16px', 
                                        fontWeight: 'bold',
                                        color: darkMode ? '#fff' : '#333'
                                    }}>Main Social Media Platform</label>
                                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                                        {/* Custom Radio Button for Instagram */}
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <div
                                                onClick={() => setUserEditForm(prev => ({
                                                    ...prev,
                                                    socialMedia: {
                                                        ...prev.socialMedia,
                                                        mainPlatform: 'instagram'
                                                    }
                                                }))}
                                                style={{
                                                    width: '12px',
                                                    height: '12px',
                                                    borderRadius: '50%',
                                                    border: `2px solid ${darkMode ? '#6366f1' : '#1976d2'}`,
                                                    backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                {userEditForm.socialMedia.mainPlatform === 'instagram' && (
                                                    <div style={{
                                                        width: '7px',
                                                        height: '7px',
                                                        borderRadius: '50%',
                                                        backgroundColor: darkMode ? '#6366f1' : '#1976d2'
                                                    }} />
                                                )}
                                            </div>
                                            <span style={{ 
                                                fontSize: '14px',
                                                color: darkMode ? '#fff' : '#333'
                                            }}>Instagram</span>
                                        </label>

                                        {/* Custom Radio Button for TikTok */}
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <div
                                                onClick={() => setUserEditForm(prev => ({
                                                    ...prev,
                                                    socialMedia: {
                                                        ...prev.socialMedia,
                                                        mainPlatform: 'tiktok'
                                                    }
                                                }))}
                                                style={{
                                                    width: '12px',
                                                    height: '12px',
                                                    borderRadius: '50%',
                                                    border: `2px solid ${darkMode ? '#6366f1' : '#1976d2'}`,
                                                    backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                {userEditForm.socialMedia.mainPlatform === 'tiktok' && (
                                                    <div style={{
                                                        width: '7px',
                                                        height: '7px',
                                                        borderRadius: '50%',
                                                        backgroundColor: darkMode ? '#6366f1' : '#1976d2'
                                                    }} />
                                                )}
                                            </div>
                                            <span style={{ 
                                                fontSize: '14px',
                                                color: darkMode ? '#fff' : '#333'
                                            }}>TikTok</span>
                                        </label>

                                        {/* Custom Radio Button for YouTube */}
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <div
                                                onClick={() => setUserEditForm(prev => ({
                                                    ...prev,
                                                    socialMedia: {
                                                        ...prev.socialMedia,
                                                        mainPlatform: 'youtube'
                                                    }
                                                }))}
                                                style={{
                                                    width: '12px',
                                                    height: '12px',
                                                    borderRadius: '50%',
                                                    border: `2px solid ${darkMode ? '#6366f1' : '#1976d2'}`,
                                                    backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                {userEditForm.socialMedia.mainPlatform === 'youtube' && (
                                                    <div style={{
                                                        width: '7px',
                                                        height: '7px',
                                                        borderRadius: '50%',
                                                        backgroundColor: darkMode ? '#6366f1' : '#1976d2'
                                                    }} />
                                                )}
                                            </div>
                                            <span style={{ 
                                                fontSize: '14px',
                                                color: darkMode ? '#fff' : '#333'
                                            }}>YouTube</span>
                                        </label>
                                    </div>
                                </div>
                                
                                {/* Instagram */}
                                <h5>Instagram</h5>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Handle</label>
                                        <input
                                            type="text"
                                            placeholder="@username"
                                            value={userEditForm.socialMedia.instagram.handle}
                                            onChange={(e) => setUserEditForm(prev => ({
                                                ...prev,
                                                socialMedia: {
                                                    ...prev.socialMedia,
                                                    instagram: {
                                                        ...prev.socialMedia.instagram,
                                                        handle: e.target.value
                                                    }
                                                }
                                            }))}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                borderRadius: '5px',
                                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                                backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                                color: darkMode ? '#fff' : '#333',
                                                fontFamily: 'inherit',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Current Followers</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={userEditForm.socialMedia.instagram.currentFollowers}
                                            onChange={(e) => setUserEditForm(prev => ({
                                                ...prev,
                                                socialMedia: {
                                                    ...prev.socialMedia,
                                                    instagram: {
                                                        ...prev.socialMedia.instagram,
                                                        currentFollowers: parseInt(e.target.value) || 0
                                                    }
                                                }
                                            }))}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                borderRadius: '5px',
                                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                                backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                                color: darkMode ? '#fff' : '#333',
                                                fontFamily: 'inherit',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Target Followers</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={userEditForm.socialMedia.instagram.targetFollowers}
                                            onChange={(e) => setUserEditForm(prev => ({
                                                ...prev,
                                                socialMedia: {
                                                    ...prev.socialMedia,
                                                    instagram: {
                                                        ...prev.socialMedia.instagram,
                                                        targetFollowers: parseInt(e.target.value) || 0
                                                    }
                                                }
                                            }))}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                borderRadius: '5px',
                                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                                backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                                color: darkMode ? '#fff' : '#333',
                                                fontFamily: 'inherit',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* TikTok */}
                                <h5>TikTok</h5>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Handle</label>
                                        <input
                                            type="text"
                                            placeholder="@username"
                                            value={userEditForm.socialMedia.tiktok.handle}
                                            onChange={(e) => setUserEditForm(prev => ({
                                                ...prev,
                                                socialMedia: {
                                                    ...prev.socialMedia,
                                                    tiktok: {
                                                        ...prev.socialMedia.tiktok,
                                                        handle: e.target.value
                                                    }
                                                }
                                            }))}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                borderRadius: '5px',
                                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                                backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                                color: darkMode ? '#fff' : '#333',
                                                fontFamily: 'inherit',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Current Followers</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={userEditForm.socialMedia.tiktok.currentFollowers}
                                            onChange={(e) => setUserEditForm(prev => ({
                                                ...prev,
                                                socialMedia: {
                                                    ...prev.socialMedia,
                                                    tiktok: {
                                                        ...prev.socialMedia.tiktok,
                                                        currentFollowers: parseInt(e.target.value) || 0
                                                    }
                                                }
                                            }))}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                borderRadius: '5px',
                                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                                backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                                color: darkMode ? '#fff' : '#333',
                                                fontFamily: 'inherit',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Target Followers</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={userEditForm.socialMedia.tiktok.targetFollowers}
                                            onChange={(e) => setUserEditForm(prev => ({
                                                ...prev,
                                                socialMedia: {
                                                    ...prev.socialMedia,
                                                    tiktok: {
                                                        ...prev.socialMedia.tiktok,
                                                        targetFollowers: parseInt(e.target.value) || 0
                                                    }
                                                }
                                            }))}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                borderRadius: '5px',
                                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                                backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                                color: darkMode ? '#fff' : '#333',
                                                fontFamily: 'inherit',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* YouTube */}
                                <h5>YouTube</h5>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Handle</label>
                                        <input
                                            type="text"
                                            placeholder="@username"
                                            value={userEditForm.socialMedia.youtube.handle}
                                            onChange={(e) => setUserEditForm(prev => ({
                                                ...prev,
                                                socialMedia: {
                                                    ...prev.socialMedia,
                                                    youtube: {
                                                        ...prev.socialMedia.youtube,
                                                        handle: e.target.value
                                                    }
                                                }
                                            }))}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                borderRadius: '5px',
                                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                                backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                                color: darkMode ? '#fff' : '#333',
                                                fontFamily: 'inherit',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Current Followers</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={userEditForm.socialMedia.youtube.currentFollowers}
                                            onChange={(e) => setUserEditForm(prev => ({
                                                ...prev,
                                                socialMedia: {
                                                    ...prev.socialMedia,
                                                    youtube: {
                                                        ...prev.socialMedia.youtube,
                                                        currentFollowers: parseInt(e.target.value) || 0
                                                    }
                                                }
                                            }))}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                borderRadius: '5px',
                                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                                backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                                color: darkMode ? '#fff' : '#333',
                                                fontFamily: 'inherit',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Target Followers</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={userEditForm.socialMedia.youtube.targetFollowers}
                                            onChange={(e) => setUserEditForm(prev => ({
                                                ...prev,
                                                socialMedia: {
                                                    ...prev.socialMedia,
                                                    youtube: {
                                                        ...prev.socialMedia.youtube,
                                                        targetFollowers: parseInt(e.target.value) || 0
                                                    }
                                                }
                                            }))}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                borderRadius: '5px',
                                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                                backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                                color: darkMode ? '#fff' : '#333',
                                                fontFamily: 'inherit',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="video-production-section">
                                <h4>Video Production (Admin Only)</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Current Videos Per Month</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={userEditForm.videoProduction.currentVideosPerMonth}
                                            onChange={(e) => setUserEditForm(prev => ({
                                                ...prev,
                                                videoProduction: {
                                                    ...prev.videoProduction,
                                                    currentVideosPerMonth: parseInt(e.target.value) || 0
                                                }
                                            }))}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                borderRadius: '5px',
                                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                                backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                                color: darkMode ? '#fff' : '#333',
                                                fontFamily: 'inherit',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Target Videos Per Month</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={userEditForm.videoProduction.targetVideosPerMonth}
                                            onChange={(e) => setUserEditForm(prev => ({
                                                ...prev,
                                                videoProduction: {
                                                    ...prev.videoProduction,
                                                    targetVideosPerMonth: parseInt(e.target.value) || 0
                                                }
                                            }))}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                borderRadius: '5px',
                                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                                backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                                color: darkMode ? '#fff' : '#333',
                                                fontFamily: 'inherit',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="edit-actions">
                                <button onClick={() => handleUserProfileUpdate(selectedUser._id)}>Save</button>
                                <button onClick={() => setEditingUser(null)}>Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <div className="user-profile-display">
                            <div className="profile-section">
                                <h4>Profile</h4>
                                <p><strong>Current Situation:</strong> {selectedUser.profile?.currentSituation || 'Not specified'}</p>
                                <p><strong>Short Term Goals:</strong> {selectedUser.profile?.shortTermGoals || 'Not specified'}</p>
                                <p><strong>Long Term Goals:</strong> {selectedUser.profile?.longTermGoals || 'Not specified'}</p>
                                <p><strong>Website URL:</strong> {selectedUser.profile?.websiteUrl ? (
                                    <a href={selectedUser.profile.websiteUrl} target="_blank" rel="noopener noreferrer" style={{color: '#007bff', textDecoration: 'underline'}}>
                                        {selectedUser.profile.websiteUrl}
                                    </a>
                                ) : 'Not specified'}</p>
                            </div>
                            <div className="prospecting-section">
                                <h4>Business Profile</h4>
                                <p><strong>Current Role:</strong> {selectedUser.prospectingProfile?.currentRole || 'Not specified'}</p>
                                <p><strong>Company Purpose:</strong> {selectedUser.prospectingProfile?.companyPurpose || 'Not specified'}</p>
                                <p><strong>Target Audience:</strong> {selectedUser.prospectingProfile?.targetAudience || 'Not specified'}</p>
                                <p><strong>How do you want to be perceived:</strong> {selectedUser.prospectingProfile?.howToBePerceived || 'Not specified'}</p>
                                <p><strong>Current Marketing Strategy:</strong> {selectedUser.prospectingProfile?.currentMarketingStrategy || 'Not specified'}</p>
                                <p><strong>Content Ideas:</strong> {selectedUser.prospectingProfile?.contentIdeas || 'Not specified'}</p>
                                <p><strong>Instagram URL:</strong> {selectedUser.prospectingProfile?.instagramUrl ? (
                                    <a href={selectedUser.prospectingProfile.instagramUrl} target="_blank" rel="noopener noreferrer" style={{color: '#007bff', textDecoration: 'underline'}}>
                                        {selectedUser.prospectingProfile.instagramUrl}
                                    </a>
                                ) : 'Not specified'}</p>
                                <p><strong>Brands / Accounts that inspire you:</strong> {selectedUser.prospectingProfile?.inspiringBrands || 'Not specified'}</p>
                                <p><strong>Competitors and main differentiation:</strong> {selectedUser.prospectingProfile?.competitors || 'Not specified'}</p>
                                <p><strong>Your target's main challenge and how you solve them:</strong> {selectedUser.prospectingProfile?.targetChallenge || 'Not specified'}</p>
                            </div>
                            <div className="additional-info-section">
                                <h4>Additional Information</h4>
                                <p>{selectedUser.additionalInformation || 'No additional information'}</p>
                            </div>
                            <div className="custom-font-section">
                                <h4>Custom Font (Admin Only)</h4>
                                <p><strong>Custom Font URL:</strong> {selectedUser.profile?.customFontUrl ? (
                                    <a href={selectedUser.profile.customFontUrl} target="_blank" rel="noopener noreferrer" style={{color: '#007bff', textDecoration: 'underline'}}>
                                        {selectedUser.profile.customFontUrl}
                                    </a>
                                ) : 'Not specified'}</p>
                            </div>
                            <div className="content-strategy-section">
                                <h4>Content Ideas (Admin Only)</h4>
                                <p><strong>Content Ideas:</strong></p>
                                <div style={{
                                    backgroundColor: darkMode ? '#2A2B2F' : '#f8f9fa',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    borderRadius: '5px',
                                    padding: '10px',
                                    marginTop: '5px',
                                    whiteSpace: 'pre-wrap',
                                    fontSize: '14px',
                                    lineHeight: '1.5'
                                }}>
                                    {selectedUser.profile?.contentStrategy || 'No content ideas defined'}
                                </div>
                            </div>
                            <div className="social-media-section">
                                <h4>Social Media (Admin Only)</h4>
                                <div style={{ marginBottom: '20px' }}>
                                    <p><strong>Main Platform:</strong> 
                                        {selectedUser.socialMedia?.mainPlatform === 'instagram' && ' 📸 Instagram'}
                                        {selectedUser.socialMedia?.mainPlatform === 'tiktok' && ' 🎵 TikTok'}
                                        {selectedUser.socialMedia?.mainPlatform === 'youtube' && ' 📺 YouTube'}
                                        {!selectedUser.socialMedia?.mainPlatform && ' Not specified'}
                                    </p>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '15px' }}>
                                    <div>
                                        <h5 style={{ marginBottom: '10px', color: darkMode ? '#fff' : '#333' }}>Instagram</h5>
                                        <p><strong>Handle:</strong> {selectedUser.socialMedia?.instagram?.handle || 'Not specified'}</p>
                                        <p><strong>Current Followers:</strong> {selectedUser.socialMedia?.instagram?.currentFollowers?.toLocaleString() || '0'}</p>
                                        <p><strong>Target Followers:</strong> {selectedUser.socialMedia?.instagram?.targetFollowers?.toLocaleString() || '0'}</p>
                                    </div>
                                    <div>
                                        <h5 style={{ marginBottom: '10px', color: darkMode ? '#fff' : '#333' }}>TikTok</h5>
                                        <p><strong>Handle:</strong> {selectedUser.socialMedia?.tiktok?.handle || 'Not specified'}</p>
                                        <p><strong>Current Followers:</strong> {selectedUser.socialMedia?.tiktok?.currentFollowers?.toLocaleString() || '0'}</p>
                                        <p><strong>Target Followers:</strong> {selectedUser.socialMedia?.tiktok?.targetFollowers?.toLocaleString() || '0'}</p>
                                    </div>
                                    <div>
                                        <h5 style={{ marginBottom: '10px', color: darkMode ? '#fff' : '#333' }}>YouTube</h5>
                                        <p><strong>Handle:</strong> {selectedUser.socialMedia?.youtube?.handle || 'Not specified'}</p>
                                        <p><strong>Current Followers:</strong> {selectedUser.socialMedia?.youtube?.currentFollowers?.toLocaleString() || '0'}</p>
                                        <p><strong>Target Followers:</strong> {selectedUser.socialMedia?.youtube?.targetFollowers?.toLocaleString() || '0'}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="video-production-section">
                                <h4>Video Production (Admin Only)</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '15px' }}>
                                    <div>
                                        <p><strong>Current Videos Per Month:</strong> {selectedUser.videoProduction?.currentVideosPerMonth || '0'}</p>
                                    </div>
                                    <div>
                                        <p><strong>Target Videos Per Month:</strong> {selectedUser.videoProduction?.targetVideosPerMonth || '0'}</p>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Activity Logs Section */}
                            <div className="activity-logs-section">
                                <h4>Activity Logs (Admin Only)</h4>
                                <div style={{ 
                                    maxHeight: '300px', 
                                    overflowY: 'auto', 
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5', 
                                    borderRadius: '5px', 
                                    padding: '10px',
                                    backgroundColor: darkMode ? '#2A2B2F' : '#f8f9fa',
                                    marginBottom: '15px'
                                }}>
                                    {selectedUser.logs && selectedUser.logs.length > 0 ? (
                                        <div>
                                            {selectedUser.logs
                                                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                                                .map((log, index) => (
                                                    <div key={index} style={{ 
                                                        marginBottom: '8px', 
                                                        paddingBottom: '8px', 
                                                        borderBottom: index < selectedUser.logs.length - 1 ? (darkMode ? '1px solid #444' : '1px solid #e0e0e0') : 'none',
                                                        fontSize: '14px'
                                                    }}>
                                                        <div style={{ 
                                                            fontWeight: 'bold', 
                                                            marginBottom: '2px',
                                                            color: darkMode ? '#fff' : '#333'
                                                        }}>
                                                            {log.message}
                                                        </div>
                                                        <div style={{ 
                                                            fontSize: '12px', 
                                                            color: darkMode ? '#999' : '#666',
                                                            fontStyle: 'italic'
                                                        }}>
                                                            {new Date(log.timestamp).toLocaleString()}
                                                        </div>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    ) : (
                                        <p style={{ 
                                            color: darkMode ? '#999' : '#666',
                                            fontStyle: 'italic',
                                            margin: 0
                                        }}>
                                            No activity logs yet.
                                        </p>
                                    )}
                                </div>
                            </div>
                            <button 
                                className="edit-profile-btn"
                                onClick={() => {
                                    setEditingUser(selectedUser._id);
                                    setUserEditForm({
                                        currentSituation: selectedUser.profile?.currentSituation || '',
                                        shortTermGoals: selectedUser.profile?.shortTermGoals || '',
                                        longTermGoals: selectedUser.profile?.longTermGoals || '',
                                        websiteUrl: selectedUser.profile?.websiteUrl || '',
                                        currentRole: selectedUser.prospectingProfile?.currentRole || '',
                                        companyPurpose: selectedUser.prospectingProfile?.companyPurpose || '',
                                        targetAudience: selectedUser.prospectingProfile?.targetAudience || '',
                                        howToBePerceived: selectedUser.prospectingProfile?.howToBePerceived || '',
                                        currentMarketingStrategy: selectedUser.prospectingProfile?.currentMarketingStrategy || '',
                                        contentIdeas: selectedUser.prospectingProfile?.contentIdeas || '',
                                        instagramUrl: selectedUser.prospectingProfile?.instagramUrl || '',
                                        inspiringBrands: selectedUser.prospectingProfile?.inspiringBrands || '',
                                        competitors: selectedUser.prospectingProfile?.competitors || '',
                                        targetChallenge: selectedUser.prospectingProfile?.targetChallenge || '',
                                        additionalInformation: selectedUser.additionalInformation || '',
                                        customFontUrl: selectedUser.profile?.customFontUrl || '',
                                        contentStrategy: selectedUser.profile?.contentStrategy || '',
                                        socialMedia: {
                                            mainPlatform: selectedUser.socialMedia?.mainPlatform || 'instagram',
                                            instagram: {
                                                handle: selectedUser.socialMedia?.instagram?.handle || '',
                                                currentFollowers: selectedUser.socialMedia?.instagram?.currentFollowers || 0,
                                                targetFollowers: selectedUser.socialMedia?.instagram?.targetFollowers || 0
                                            },
                                            tiktok: {
                                                handle: selectedUser.socialMedia?.tiktok?.handle || '',
                                                currentFollowers: selectedUser.socialMedia?.tiktok?.currentFollowers || 0,
                                                targetFollowers: selectedUser.socialMedia?.tiktok?.targetFollowers || 0
                                            },
                                            youtube: {
                                                handle: selectedUser.socialMedia?.youtube?.handle || '',
                                                currentFollowers: selectedUser.socialMedia?.youtube?.currentFollowers || 0,
                                                targetFollowers: selectedUser.socialMedia?.youtube?.targetFollowers || 0
                                            }
                                        },
                                        videoProduction: {
                                            currentVideosPerMonth: selectedUser.videoProduction?.currentVideosPerMonth || 0,
                                            targetVideosPerMonth: selectedUser.videoProduction?.targetVideosPerMonth || 0
                                        }
                                    });
                                }}
                            >
                                <Edit2 size={14} /> Edit Profile
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="video-studio-section">
                    {renderStudioHeader()}
                    <div className="script-hooks-section">
                        <h4>Script Hooks</h4>
                        <div className="hooks-list">
                            {scriptHooks.map((hook, index) => (
                                <div key={index} className="hook-item">
                                    <input
                                        type="text"
                                        value={hook}
                                        onChange={(e) => {
                                            const newHooks = [...scriptHooks];
                                            newHooks[index] = e.target.value;
                                            setScriptHooks(newHooks);
                                        }}
                                    />
                                    <button 
                                        className="remove-hook-btn"
                                        onClick={() => {
                                            setScriptHooks(scriptHooks.filter((_, i) => i !== index));
                                        }}
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ))}
                            <button 
                                className="add-hook-btn"
                                onClick={() => setScriptHooks([...scriptHooks, ''])}
                            >
                                <Plus size={14} /> Add Hook
                            </button>
                        </div>
                    </div>

                    {renderGeneratedScripts()}
                </div>
            )}
        </div>
    );

    // Update the user card rendering in the users grid
    const renderUserCard = (user) => (
        <div 
            key={user._id} 
            className={`user-card ${selectedUser?._id === user._id ? 'selected' : ''}`}
            onClick={() => handleUserSelect(user)}
        >
            <div className="user-info">
                <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px'}}>
                    <img src={user.picture} alt={user.name} style={{width: '20px', height: '20px', borderRadius: '50%', margin: '0px'}} />
                    <h3 style={{margin: '0', fontSize: '14px'}}>{user.name}</h3>
                </div>
                <p style={{margin: '0', marginTop: '5px', fontSize: '12px'}}>{user.email}</p>
            </div>
        </div>
    );

    // Update the script card JSX to include video generation and publishing
    const renderScriptCard = (script, index) => (
        <div key={index} className="script-card" style={{
            border: `2px solid ${script.type === 'voiceover' ? 'rgba(139, 92, 246, 0.3)' : 'rgba(16, 185, 129, 0.3)'}`,
            borderRadius: '8px',
            padding: '16px',
            backgroundColor: 'var(--input-bg)',
            boxShadow: `0 2px 8px ${script.type === 'voiceover' ? 'rgba(139, 92, 246, 0.1)' : 'rgba(16, 185, 129, 0.1)'}`
        }}>
            <div className="script-header" style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'}}>
                <div className="script-type" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    backgroundColor: script.type === 'voiceover' ? 'rgba(139, 92, 246, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                    border: `1px solid ${script.type === 'voiceover' ? 'rgba(139, 92, 246, 0.3)' : 'rgba(16, 185, 129, 0.3)'}`
                }}>
                    {script.type === 'voiceover' ? (
                        <Mic size={14} style={{ color: '#8b5cf6' }} />
                    ) : (
                        <Type size={14} style={{ color: '#10b981' }} />
                    )}
                    <span style={{
                        fontSize: '12px',
                        fontWeight: '500',
                        color: script.type === 'voiceover' ? '#8b5cf6' : '#10b981',
                        textTransform: 'capitalize'
                    }}>
                        {script.type}
                    </span>
                </div>
                <button 
                    className="delete-script-btn"
                    onClick={() => handleDeleteScript(script._id)}
                    style={{
                        backgroundColor: 'var(--input-bg)',
                        color: 'var(--text-color)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        opacity: '0.9',
                        transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.backgroundColor = '#ff4444';
                        e.currentTarget.style.color = 'white';
                        e.currentTarget.style.borderColor = '#ff4444';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.opacity = '0.9';
                        e.currentTarget.style.backgroundColor = 'var(--input-bg)';
                        e.currentTarget.style.color = 'var(--text-color)';
                        e.currentTarget.style.borderColor = 'var(--input-border)';
                    }}
                >
                    <Trash size={14} />
                </button>
            </div>
            
            {/* Script Content - Show both versions for voiceover scripts */}
            {script.type === 'voiceover' && script.contentAlt ? (
                <div style={{ marginBottom: '16px' }}>
                    {/* Version A */}
                    <div style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <input
                                type="radio"
                                id={`version-main-${script._id}`}
                                name={`version-${script._id}`}
                                checked={script.selectedVersion === 'main'}
                                onChange={() => handleScriptVersionChange(script._id, 'main')}
                                style={{ cursor: 'pointer' }}
                            />
                            <label 
                                htmlFor={`version-main-${script._id}`}
                                style={{ 
                                    fontSize: '14px', 
                                    fontWeight: '500', 
                                    color: 'var(--text-color)',
                                    cursor: 'pointer'
                                }}
                            >
                                Version A (Structured)
                            </label>
                        </div>
                        <TextareaAutosize
                            value={script.content}
                            minRows={4}
                            maxRows={8}
                            style={{
                                width: '100%',
                                resize: 'none',
                                boxSizing: 'border-box',
                                padding: '8px',
                                borderRadius: '4px',
                                border: script.selectedVersion === 'main' 
                                    ? '2px solid #8b5cf6' 
                                    : darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                color: darkMode ? '#fff' : '#333',
                                fontFamily: 'inherit',
                                fontSize: '14px',
                                opacity: script.selectedVersion === 'main' ? 1 : 0.7
                            }}
                            onChange={(e) => {
                                const newContent = e.target.value;
                                const newScripts = [...generatedScripts];
                                newScripts[index] = {
                                    ...newScripts[index],
                                    content: newContent
                                };
                                setGeneratedScripts(newScripts);
                                
                                if (debouncedSave.current) {
                                    debouncedSave.current(script._id, newContent, script.description);
                                }
                            }}
                            onBlur={(e) => {
                                if (debouncedSave.current) {
                                    debouncedSave.current.flush();
                                }
                            }}
                        />
                    </div>

                    {/* Version B */}
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <input
                                type="radio"
                                id={`version-alt-${script._id}`}
                                name={`version-${script._id}`}
                                checked={script.selectedVersion === 'alt'}
                                onChange={() => handleScriptVersionChange(script._id, 'alt')}
                                style={{ cursor: 'pointer' }}
                            />
                            <label 
                                htmlFor={`version-alt-${script._id}`}
                                style={{ 
                                    fontSize: '14px', 
                                    fontWeight: '500', 
                                    color: 'var(--text-color)',
                                    cursor: 'pointer'
                                }}
                            >
                                Version B (Conversational)
                            </label>
                        </div>
                        <TextareaAutosize
                            value={script.contentAlt}
                            minRows={4}
                            maxRows={8}
                            style={{
                                width: '100%',
                                resize: 'none',
                                boxSizing: 'border-box',
                                padding: '8px',
                                borderRadius: '4px',
                                border: script.selectedVersion === 'alt' 
                                    ? '2px solid #8b5cf6' 
                                    : darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                color: darkMode ? '#fff' : '#333',
                                fontFamily: 'inherit',
                                fontSize: '14px',
                                opacity: script.selectedVersion === 'alt' ? 1 : 0.7
                            }}
                                                         onChange={(e) => {
                                 const newContentAlt = e.target.value;
                                 const newScripts = [...generatedScripts];
                                 newScripts[index] = {
                                     ...newScripts[index],
                                     contentAlt: newContentAlt
                                 };
                                 setGeneratedScripts(newScripts);
                                 
                                 // Debounce the save to backend
                                 if (debouncedSaveAlt.current) {
                                     debouncedSaveAlt.current(script._id, newContentAlt);
                                 }
                             }}
                             onBlur={(e) => {
                                 // Force save on blur if there's a pending debounced save
                                 if (debouncedSaveAlt.current) {
                                     debouncedSaveAlt.current.flush();
                                 }
                             }}
                        />
                    </div>
                </div>
            ) : (
                /* Single version for text scripts or voiceover scripts without alt version */
                <TextareaAutosize
                    value={script.content}
                    minRows={4}
                    maxRows={8}
                    style={{
                        width: '100%',
                        resize: 'none',
                        boxSizing: 'border-box',
                        padding: '8px',
                        borderRadius: '4px',
                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                        backgroundColor: darkMode ? '#2A2B2F' : 'white',
                        color: darkMode ? '#fff' : '#333',
                        fontFamily: 'inherit',
                        fontSize: '14px'
                    }}
                    onChange={(e) => {
                        const newContent = e.target.value;
                        const newScripts = [...generatedScripts];
                        newScripts[index] = {
                            ...newScripts[index],
                            content: newContent
                        };
                        setGeneratedScripts(newScripts);
                        
                        if (debouncedSave.current) {
                            debouncedSave.current(script._id, newContent, script.description);
                        }
                    }}
                    onBlur={(e) => {
                        if (debouncedSave.current) {
                            debouncedSave.current.flush();
                        }
                    }}
                />
            )}
            {/* Description field for text-type scripts */}
            {script.type === 'text' && (
                <div style={{marginTop: '12px'}}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '6px'
                    }}>
                        <label style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            color: 'var(--text-color)'
                        }}>
                            Social Media Description:
                        </label>
                        <button 
                            className="regenerate-description-btn"
                            onClick={() => handleRegenerateDescription(index)}
                            style={{
                                backgroundColor: 'var(--button-bg)',
                                color: 'var(--button-text)',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '12px',
                                opacity: '0.9',
                                transition: 'opacity 0.2s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                        >
                            <RefreshCw size={12} />
                            Regenerate
                        </button>
                    </div>
                    <TextareaAutosize
                        value={script.description || ''}
                        placeholder="Social media description with hashtags..."
                        minRows={3}
                        maxRows={6}
                        style={{
                            width: '100%',
                            resize: 'none',
                            boxSizing: 'border-box',
                            padding: '8px',
                            borderRadius: '4px',
                            border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                            backgroundColor: darkMode ? '#2A2B2F' : 'white',
                            color: darkMode ? '#fff' : '#333',
                            fontFamily: 'inherit',
                            fontSize: '14px'
                        }}
                        onChange={(e) => {
                            const newDescription = e.target.value;
                            // Update local state immediately for responsive UI
                            const newScripts = [...generatedScripts];
                            newScripts[index] = {
                                ...newScripts[index],
                                description: newDescription
                            };
                            setGeneratedScripts(newScripts);
                            
                            // Debounce the save to backend
                            if (debouncedSave.current) {
                                debouncedSave.current(script._id, script.content, newDescription);
                            }
                        }}
                        onBlur={(e) => {
                            // Force save on blur if there's a pending debounced save
                            if (debouncedSave.current) {
                                debouncedSave.current.flush();
                            }
                        }}
                    />
                </div>
            )}
            <div className="script-actions">
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'left', gap: '8px'}}>
                        <label style={{ fontSize: '12px', color: 'var(--text-color)', opacity: '0.7', alignSelf: 'flex-start' }}>Regeneration Prompt</label>
                        <input
                            type="text"
                            value={script.regenerationPrompt || ''}
                            onChange={(e) => {
                                const newScripts = [...generatedScripts];
                                newScripts[index] = {
                                    ...newScripts[index],
                                    regenerationPrompt: e.target.value
                                };
                                setGeneratedScripts(newScripts);
                            }}
                            placeholder="e.g., 'More casual'..."
                            style={{
                                flex: 1,
                                padding: '6px 8px',
                                borderRadius: '4px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                color: darkMode ? '#fff' : '#333',
                                fontSize: '12px'
                            }}
                        />
                    </div>
                    <button 
                        className="regenerate-btn"
                        onClick={() => handleRegenerateScript(index)}
                    >
                        Regenerate Script
                    </button>
                </div>
            </div>

            {script.type === 'voiceover' && (
                <>
                    <button 
                        className="generate-voiceover-btn"
                        onClick={() => handleGenerateVoiceover(index)}
                        disabled={generatingVoiceovers.has(script._id)}
                    >
                        {generatingVoiceovers.has(script._id) ? <><span className="spinner-border" style={{marginRight:'5px'}}/>Generating...</> : script.voiceoverUrl ? 'Regenerate Voiceover' : 'Generate Voiceover'}
                    </button>
                </>
            )}
            {script.voiceoverUrl && (
                <div className="voiceover-preview">
                    <audio src={script.voiceoverUrl} controls style={{width: '100%'}} />
                </div>
            )}
            {(script.voiceoverUrl || script.type === 'text') && (
                <>
                    {script.type === 'text' && (
                        <button 
                            className="select-cut-btn"
                            onClick={() => handleSelectCut(index)}
                            style={{
                                backgroundColor: 'var(--input-bg)',
                                color: 'var(--text-color)',
                                border: '1px solid var(--input-border)',
                                borderRadius: '4px',
                                padding: '8px 16px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                opacity: '0.9',
                                transition: 'all 0.2s',
                                marginBottom: '8px',
                                width: '100%',
                                justifyContent: 'center'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.opacity = '1';
                                e.currentTarget.style.backgroundColor = 'var(--button-bg)';
                                e.currentTarget.style.color = 'var(--button-text)';
                                e.currentTarget.style.borderColor = 'var(--button-bg)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.opacity = '0.9';
                                e.currentTarget.style.backgroundColor = 'var(--input-bg)';
                                e.currentTarget.style.color = 'var(--text-color)';
                                e.currentTarget.style.borderColor = 'var(--input-border)';
                            }}
                        >
                            <Video size={14} />
                            {selectedCutForScript[script._id] ? 'Change Selected Cut' : 'Select Cut'}
                        </button>
                    )}
                    <button 
                        className="select-music-btn"
                        onClick={() => handleSelectMusic(index)}
                        style={{
                            backgroundColor: selectedMusicForScript[script._id] ? 'rgba(139, 92, 246, 0.1)' : 'var(--input-bg)',
                            color: selectedMusicForScript[script._id] ? '#8b5cf6' : 'var(--text-color)',
                            border: selectedMusicForScript[script._id] ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid var(--input-border)',
                            borderRadius: '4px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            opacity: '0.9',
                            transition: 'all 0.2s',
                            marginBottom: '8px',
                            width: '100%',
                            justifyContent: 'center'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.opacity = '1';
                            e.currentTarget.style.backgroundColor = 'var(--button-bg)';
                            e.currentTarget.style.color = 'var(--button-text)';
                            e.currentTarget.style.borderColor = 'var(--button-bg)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.opacity = '0.9';
                            e.currentTarget.style.backgroundColor = selectedMusicForScript[script._id] ? 'rgba(139, 92, 246, 0.1)' : 'var(--input-bg)';
                            e.currentTarget.style.color = selectedMusicForScript[script._id] ? '#8b5cf6' : 'var(--text-color)';
                            e.currentTarget.style.borderColor = selectedMusicForScript[script._id] ? 'rgba(139, 92, 246, 0.3)' : 'var(--input-border)';
                        }}
                    >
                        <Mic size={14} />
                        {selectedMusicForScript[script._id] ? 'Change Background Music' : 'Select Background Music'}
                    </button>
                    {selectedMusicForScript[script._id] && (
                        <div style={{
                            backgroundColor: 'rgba(139, 92, 246, 0.1)',
                            border: '1px solid rgba(139, 92, 246, 0.3)',
                            borderRadius: '4px',
                            padding: '8px 12px',
                            marginBottom: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <Mic size={12} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                            <span style={{
                                fontSize: '12px',
                                color: '#8b5cf6',
                                fontWeight: '500'
                            }}>
                                {(() => {
                                    const selectedMusic = availableMusic.find(music => music._id === selectedMusicForScript[script._id]);
                                    return selectedMusic ? selectedMusic.name : 'Selected Music';
                                })()}
                            </span>
                        </div>
                    )}
                    <button 
                        className="generate-video-btn"
                        onClick={() => handleGenerateVideo(index)}
                        disabled={generatingVideos.has(script._id)}
                    >
                        {generatingVideos.has(script._id) ? (
                            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                <span className="spinner-border" style={{marginRight:'5px'}}/>
                                {script.status === 'processing' ? (
                                    <>
                                        Generating Video
                                        {script.progress > 0 && (
                                            <span style={{marginLeft: '8px'}}>
                                                {Math.round(script.progress)}%
                                            </span>
                                        )}
                                    </>
                                ) : 'Generating...'}
                            </div>
                        ) : (script.videoUrl && script.status === 'draft' && script.progress === 100) ? 'Regenerate Video' : 'Generate Video'}
                    </button>
                </>
            )}
            {(script.status === 'processing' || generatingVideos.has(script._id)) && (
                <div className="video-generation-progress">
                    <div className="progress-bar" style={{
                        width: '100%',
                        height: '4px',
                        backgroundColor: 'var(--input-border)',
                        borderRadius: '2px',
                        marginTop: '12px',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: `${script.progress || 0}%`,
                            height: '4px',
                            backgroundColor: 'var(--button-bg)',
                            transition: 'width 0.3s ease-in-out'
                        }} />
                    </div>
                    <div className="progress-status" style={{
                        fontSize: '12px',
                        color: 'var(--text-color)',
                        opacity: '0.8',
                        marginTop: '6px',
                        textAlign: 'center'
                    }}>
                        {getVideoGenerationStatusMessage(script.progress || 0)}
                    </div>
                </div>
            )}
            {script.videoUrl && !generatingVideos.has(script._id) && script.progress === 100 && (
                <div className="video-preview" style={{objectFit: 'contain', boxSizing: 'border-box'}}>
                    <video src={script.videoUrl} controls style={{objectFit: 'contain'}} />
                </div>
            )}
            {script.videoUrl && !generatingVideos.has(script._id) && script.progress === 100 && (
                <div className="video-actions" style={{display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '8px'}}>
                    <button 
                        className="download-video-btn"
                        onClick={() => handleDownloadScriptVideo(script)}
                        style={{
                            backgroundColor: 'var(--button-bg)',
                            color: 'var(--button-text)',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 10px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            opacity: '0.9',
                            transition: 'opacity 0.2s',
                            fontSize: '14px',
                        }}
                        onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                        onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                    >
                        <Download size={14} />
                    </button>
                    <button 
                        className={`publish-btn ${script.status === 'published' ? 'published' : ''}`}
                        onClick={() => handlePublishVideo(index)}
                        disabled={script.status === 'published' || script.status === 'processing'}
                        style={{display: 'flex', justifyContent: 'center'}}
                    >
                        {script.status === 'published' ? 'Published' : "Publish to User's Video Library"} <Upload size={14} style={{marginLeft: '5px', alignSelf: 'center'}} />
                    </button>
                </div>
            )}
        </div>
    );

    // Update the generated scripts section to use the new renderScriptCard function
    const renderGeneratedScripts = () => (
        <div className="generated-scripts-section">
            <h4>Generated Scripts</h4>
                        {generatedScripts.length > 0 && (
                generatedScripts.some(script => script.type === 'voiceover' && !script.voiceoverUrl) ||
                generatedScripts.some(script => (script.voiceoverUrl || script.type === 'text') && !(script.videoUrl && script.status === 'draft' && script.progress === 100))
            ) && (
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                    {generatedScripts.some(script => script.type === 'voiceover' && !script.voiceoverUrl) && (
                        <button 
                            className="generate-all-voiceovers-btn"
                            onClick={handleGenerateAllVoiceovers}
                            style={{
                                backgroundColor: 'var(--button-bg)',
                                color: 'var(--button-text)',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '8px 16px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                opacity: '0.9',
                                transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                        >
                            <Mic size={16} />
                            Generate All Voiceovers
                        </button>
                    )}
                    {generatedScripts.some(script => (script.voiceoverUrl || script.type === 'text') && !(script.videoUrl && script.status === 'draft' && script.progress === 100)) && (
                        <button 
                            className="generate-all-videos-btn"
                            onClick={handleGenerateAllVideos}
                            style={{
                                backgroundColor: 'var(--button-bg)',
                                color: 'var(--button-text)',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '8px 16px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                opacity: '0.9',
                                transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                        >
                            <Video size={16} />
                            Generate All Videos
                        </button>
                    )}
                    {/* Auto-Cut Toggle */}
                    <div className="auto-cut-toggle" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '8px',
                        padding: '8px 16px'
                    }}>
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>Auto-Cut</span>
                        <div 
                            className="toggle-switch"
                            onClick={() => handleAutoCutToggle(!autoCutEnabled)}
                            style={{
                                width: '44px',
                                height: '24px',
                                backgroundColor: autoCutEnabled ? 'var(--button-bg)' : '#ccc',
                                borderRadius: '12px',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: 'background-color 0.3s ease'
                            }}
                        >
                            <div style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                position: 'absolute',
                                top: '2px',
                                left: autoCutEnabled ? '22px' : '2px',
                                transition: 'left 0.3s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }} />
                        </div>
                        <span style={{ 
                            fontSize: '12px', 
                            color: 'var(--text-color)', 
                            opacity: '0.7',
                            maxWidth: '200px'
                        }}>
                            {autoCutEnabled ? 'Programmatic cuts (0.5-1s clips)' : 'User-uploaded cuts'}
                        </span>
                    </div>
                </div>
            )}
            {isGeneratingScripts ? (
                <div className="generation-status">
                    <div className="waiting-messages" style={{
                        padding: '12px',
                        backgroundColor: 'var(--input-bg)',
                        borderRadius: '4px',
                        marginBottom: '16px',
                        border: '1px solid #dfe1e5',
                        position: 'relative',
                        overflow: 'hidden',
                        height: '60px'
                    }}>
                        {WAITING_MESSAGES.map((message, index) => (
                            <div
                                key={index}
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '12px',
                                    right: '12px',
                                    transform: `translateY(-50%) translateY(${(index - currentWaitingMessage) * 60}px)`,
                                    opacity: index === currentWaitingMessage ? 1 : 0,
                                    transition: 'all 0.5s ease-in-out',
                                    textAlign: 'center',
                                    color: '#666'
                                }}
                            >
                                {message}
                            </div>
                        ))}
                    </div>

                </div>
            ) : generatedScripts.length > 0 ? (
                <div className="scripts-grid">
                    {generatedScripts.map((script, index) => renderScriptCard(script, index))}
                </div>
            ) : (
                <div className="no-scripts">
                    <p>No scripts generated yet. Click "Generate 16 Scripts" to start.</p>
                </div>
            )}
        </div>
    );

    // Update the studio header to include clear all button and Auto-Cut toggle
    const renderStudioHeader = () => (
        <div className="studio-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <div>
                    <h3>Video Content Generation</h3>
                </div>
                
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    {/* Published Context Toggle */}
                    <div className="published-context-toggle" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '8px',
                        padding: '8px 16px'
                    }}>
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>Include Published Context</span>
                        <div 
                            className="toggle-switch"
                            onClick={() => setIncludePublishedContext(!includePublishedContext)}
                            style={{
                                width: '44px',
                                height: '24px',
                                backgroundColor: includePublishedContext ? 'var(--button-bg)' : '#ccc',
                                borderRadius: '12px',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: 'background-color 0.3s ease'
                            }}
                        >
                            <div style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                position: 'absolute',
                                top: '2px',
                                left: includePublishedContext ? '22px' : '2px',
                                transition: 'left 0.3s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }} />
                        </div>
                        <span style={{ 
                            fontSize: '12px', 
                            color: 'var(--text-color)', 
                            opacity: '0.7',
                            maxWidth: '200px'
                        }}>
                            {includePublishedContext ? 'Include published video descriptions' : 'Use only user profile'}
                        </span>
                    </div>

                    
                </div>
            </div>
            
            {/* Creativity Slider */}
            <div style={{ marginTop: '12px', marginBottom: '8px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: 'var(--text-color)', opacity: '0.8' }}>
                    Creativity Level:
                    <span
                        style={{
                            fontWeight: 'bold',
                            background: 'linear-gradient(to right, var(--text-color) 20%, #AB5898 70%, #5C4BD3 80%)',
                            WebkitBackgroundClip: 'text',
                            backgroundClip: 'text',
                            color: 'transparent',
                            marginLeft: '8px'
                        }}
                    >
                        {Math.round(dragCreativityValue) === 1 ? 'Very Precise' :
                        Math.round(dragCreativityValue) === 2 ? 'More Precise' :
                        Math.round(dragCreativityValue) === 3 ? 'Balanced' :
                        Math.round(dragCreativityValue) === 4 ? 'More Creative' :
                        Math.round(dragCreativityValue) === 5 ? 'Very Creative' :
                        `${Math.round(dragCreativityValue)}`}
                    </span>
                </label>
                <div style={{
                    backgroundColor: 'var(--input-bg)',
                    borderRadius: '8px',
                    padding: '12px',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: '1px solid var(--input-border)'
                }}>
                    {/* Left label */}
                    <span style={{ 
                        fontSize: '12px',
                        color: 'var(--text-color)',
                        opacity: '0.7',
                        textAlign: 'left',
                        minWidth: '60px'
                    }}>
                        Precise
                    </span>
                    {/* Slider */}
                    <div style={{ position: 'relative', width: '100%', marginLeft: '20px', marginRight: '20px' }}>
                        <div className="tick-slider-container">
                            <input
                                type="range"
                                min="1"
                                max="5"
                                step="0.005"
                                value={dragCreativityValue}
                                onChange={handleCreativitySliderDrag}
                                onMouseUp={handleCreativitySliderRelease}
                                onTouchEnd={handleCreativitySliderRelease}
                                style={{
                                    width: '100%',
                                    height: '8px',
                                    borderRadius: '8px',
                                    outline: 'none',
                                    alignSelf: 'center',
                                    margin: '0px',
                                    background: 'var(--input-border)',
                                    cursor: 'pointer'
                                }}
                            />
                                <div className="tick-marks"
                                 style={{
                                    position: 'absolute',
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'space-evenly',
                                    pointerEvents: 'none',
                                    marginLeft: '3px',
                                    marginRight: '3px'
                                 }}>
                                    <div className="tick"></div>
                                    <div className="tick"></div>
                                    <div className="tick"></div>
                                </div>
                        </div>
                    </div>
                    {/* Right label */}
                    <span style={{ 
                        fontSize: '12px',
                        color: 'var(--text-color)',
                        opacity: '0.7',
                        textAlign: 'right',
                        minWidth: '60px'
                    }}>
                        Creative
                    </span>
                </div>
            </div>

            {/* Voice Settings Dropdown */}
            <div style={{ marginTop: '12px', marginBottom: '8px' }}>
                <div style={{ 
                    cursor: 'pointer',
                    padding: '12px',
                    backgroundColor: 'var(--input-bg)',
                    border: '1px solid var(--input-border)',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }} onClick={() => setVoiceSettingsOpen(!voiceSettingsOpen)}>
                    <label style={{ 
                        fontSize: '14px', 
                        color: 'var(--text-color)', 
                        opacity: '0.8',
                        cursor: 'pointer',
                        margin: 0
                    }}>
                        Voice Settings (Voiceover Generation)
                    </label>
                    <span style={{ 
                        fontSize: '18px', 
                        color: 'var(--text-color)', 
                        opacity: '0.6',
                        transform: voiceSettingsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}>
                        ▼
                    </span>
                </div>
                
                {voiceSettingsOpen && (
                    <div style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderTop: 'none',
                        borderRadius: '0 0 8px 8px',
                        padding: '16px'
                    }}>
                        {/* Style Slider */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ 
                                display: 'block', 
                                marginBottom: '8px', 
                                fontSize: '12px', 
                                color: 'var(--text-color)', 
                                opacity: '0.8' 
                            }}>
                                Style: <span style={{ fontWeight: 'bold', color: '#8b5cf6' }}>
                                    {getVoiceSettingLabel('style', dragVoiceStyle)}
                                </span>
                            </label>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                            }}>
                                <span style={{ fontSize: '11px', opacity: '0.7', minWidth: '50px' }}>Natural</span>
                                <div style={{ position: 'relative', width: '100%' }}>
                                    <input
                                        type="range"
                                        min="1"
                                        max="3"
                                        step="0.005"
                                        value={dragVoiceStyle}
                                        onChange={(e) => handleVoiceSliderDrag('style', e.target.value)}
                                        onMouseUp={() => handleVoiceSliderRelease('style')}
                                        onTouchEnd={() => handleVoiceSliderRelease('style')}
                                        style={{
                                            width: '100%',
                                            height: '6px',
                                            borderRadius: '6px',
                                            outline: 'none',
                                            background: 'var(--input-border)',
                                            cursor: 'pointer'
                                        }}
                                    />
                                    <div style={{
                                        position: 'absolute',
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        pointerEvents: 'none',
                                        marginTop: '2px'
                                    }}>
                                        <div style={{ width: '2px', height: '3px', backgroundColor: '#fff' }}></div>
                                        <div style={{ width: '2px', height: '3px', backgroundColor: '#fff' }}></div>
                                        <div style={{ width: '2px', height: '3px', backgroundColor: '#fff' }}></div>
                                    </div>
                                </div>
                                <span style={{ fontSize: '11px', opacity: '0.7', minWidth: '50px', textAlign: 'right' }}>Dramatic</span>
                            </div>
                        </div>

                        {/* Stability Slider */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ 
                                display: 'block', 
                                marginBottom: '8px', 
                                fontSize: '12px', 
                                color: 'var(--text-color)', 
                                opacity: '0.8' 
                            }}>
                                Stability: <span style={{ fontWeight: 'bold', color: '#8b5cf6' }}>
                                    {getVoiceSettingLabel('stability', dragVoiceStability)}
                                </span>
                            </label>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                            }}>
                                <span style={{ fontSize: '11px', opacity: '0.7', minWidth: '50px' }}>Variable</span>
                                <div style={{ position: 'relative', width: '100%' }}>
                                    <input
                                        type="range"
                                        min="1"
                                        max="3"
                                        step="0.005"
                                        value={dragVoiceStability}
                                        onChange={(e) => handleVoiceSliderDrag('stability', e.target.value)}
                                        onMouseUp={() => handleVoiceSliderRelease('stability')}
                                        onTouchEnd={() => handleVoiceSliderRelease('stability')}
                                        style={{
                                            width: '100%',
                                            height: '6px',
                                            borderRadius: '6px',
                                            outline: 'none',
                                            background: 'var(--input-border)',
                                            cursor: 'pointer'
                                        }}
                                    />
                                    <div style={{
                                        position: 'absolute',
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        pointerEvents: 'none',
                                        marginTop: '2px'
                                    }}>
                                        <div style={{ width: '2px', height: '3px', backgroundColor: '#fff' }}></div>
                                        <div style={{ width: '2px', height: '3px', backgroundColor: '#fff' }}></div>
                                        <div style={{ width: '2px', height: '3px', backgroundColor: '#fff' }}></div>
                                    </div>
                                </div>
                                <span style={{ fontSize: '11px', opacity: '0.7', minWidth: '50px', textAlign: 'right' }}>Consistent</span>
                            </div>
                        </div>

                        {/* Similarity Slider */}
                        <div>
                            <label style={{ 
                                display: 'block', 
                                marginBottom: '8px', 
                                fontSize: '12px', 
                                color: 'var(--text-color)', 
                                opacity: '0.8' 
                            }}>
                                Similarity: <span style={{ fontWeight: 'bold', color: '#8b5cf6' }}>
                                    {getVoiceSettingLabel('similarity', dragVoiceSimilarity)}
                                </span>
                            </label>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                            }}>
                                <span style={{ fontSize: '11px', opacity: '0.7', minWidth: '50px' }}>Creative</span>
                                <div style={{ position: 'relative', width: '100%' }}>
                                    <input
                                        type="range"
                                        min="1"
                                        max="3"
                                        step="0.005"
                                        value={dragVoiceSimilarity}
                                        onChange={(e) => handleVoiceSliderDrag('similarity', e.target.value)}
                                        onMouseUp={() => handleVoiceSliderRelease('similarity')}
                                        onTouchEnd={() => handleVoiceSliderRelease('similarity')}
                                        style={{
                                            width: '100%',
                                            height: '6px',
                                            borderRadius: '6px',
                                            outline: 'none',
                                            background: 'var(--input-border)',
                                            cursor: 'pointer'
                                        }}
                                    />
                                    <div style={{
                                        position: 'absolute',
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        pointerEvents: 'none',
                                        marginTop: '2px'
                                    }}>
                                        <div style={{ width: '2px', height: '3px', backgroundColor: '#fff' }}></div>
                                        <div style={{ width: '2px', height: '3px', backgroundColor: '#fff' }}></div>
                                        <div style={{ width: '2px', height: '3px', backgroundColor: '#fff' }}></div>
                                    </div>
                                </div>
                                <span style={{ fontSize: '11px', opacity: '0.7', minWidth: '50px', textAlign: 'right' }}>Accurate</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Voice Selection Dropdown - only show when user doesn't have a voice */}
            {selectedUser && !selectedUser.voiceId && (
                <div style={{ marginTop: '12px', marginBottom: '8px' }}>
                    <label style={{ 
                        fontSize: '14px', 
                        color: 'var(--text-color)', 
                        opacity: '0.8',
                        display: 'block',
                        marginBottom: '8px'
                    }}>
                        Default Voice (User has no voice cloned)
                    </label>
                    <select
                        value={selectedDefaultVoice}
                        onChange={(e) => setSelectedDefaultVoice(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '12px',
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '8px',
                            color: 'var(--text-color)',
                            fontSize: '14px',
                            outline: 'none'
                        }}
                    >
                        {DEFAULT_VOICES.map(voice => (
                            <option key={voice.id} value={voice.id}>
                                {voice.name}
                            </option>
                        ))}
                    </select>
                </div>
            )}
            
            <div className="button-group" style={{ marginTop: '16px' }}>
                <button 
                    className="generate-scripts-btn"
                    onClick={handleGenerateScripts}
                    disabled={isGeneratingScripts}
                >
                    {isGeneratingScripts ? <><span className="spinner-border" style={{marginRight:'5px'}}/>Generating...</> : generatedScripts.length > 0 ? 'Generate 16 More Scripts' : 'Generate 16 Scripts'}
                </button>
                <button 
                    className="shuffle-hooks-btn"
                    onClick={handleShuffleHooks}
                >
                    Shuffle Hooks
                </button>
                <button 
                    className="upload-music-btn"
                    onClick={() => setShowMusicUploadModal(true)}
                    style={{
                        backgroundColor: 'var(--button-bg)',
                        color: 'var(--button-text)',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        opacity: '0.9',
                        transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                >
                    <Upload size={16} />
                    Upload Music
                </button>
                {generatedScripts.length > 0 && (
                    <button 
                        className="clear-scripts-btn"
                        onClick={handleClearAllScripts}
                        style={{
                            backgroundColor: 'var(--input-bg)',
                            color: 'var(--text-color)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '4px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            opacity: '0.9',
                            transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.opacity = '1';
                            e.currentTarget.style.backgroundColor = '#ff4444';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.borderColor = '#ff4444';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.opacity = '0.9';
                            e.currentTarget.style.backgroundColor = 'var(--input-bg)';
                            e.currentTarget.style.color = 'var(--text-color)';
                            e.currentTarget.style.borderColor = 'var(--input-border)';
                        }}
                    >
                        <Trash size={16} />
                        Clear All Scripts
                    </button>
                )}
            </div>
        </div>
    );

    return (
        <div className="backoffice">
            {/* Header */}
            <div className="backoffice-header">
                <h1>Admin BackOffice</h1>
                <div className="backoffice-tabs">
                    <button 
                        className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => handleTabChange('users')}
                    >
                        <User size={16} />
                        Users & Content
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'videos' ? 'active' : ''}`}
                        onClick={() => handleTabChange('videos')}
                    >
                        <Video size={16} />
                        All Videos
                    </button>
                </div>
            </div>

            {/* Error Messages */}
            {errors.length > 0 && (
                <div className="error-container">
                    {errors.map((error, index) => (
                        <div key={`error-${index}`} className="error-banner">
                            <AlertCircle size={20} />
                            {error}
                            <button onClick={() => setErrors(errors.filter((_, i) => i !== index))}>
                                <X size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Users Tab Content */}
            {activeTab === 'users' && (
                <div className="users-content">
                    <div className="users-list">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2>Users</h2>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <span style={{ 
                                    fontSize: '14px', 
                                    opacity: '0.7',
                                    color: 'var(--text-color)'
                                }}>
                                    {filteredUsers.length > 0 || selectedUserIds.length > 0 || userSearchQuery.trim() ? filteredUsers.length : users.length} / {users.length}
                                </span>
                                <button
                                    onClick={() => setShowUserFilters(!showUserFilters)}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: (selectedUserIds.length > 0 || userSearchQuery.trim()) ? '#333' : 'var(--button-bg)',
                                        borderColor: "#555",
                                        color: (selectedUserIds.length > 0 || userSearchQuery.trim()) ? 'white' : 'var(--button-text)',
                                        border: '1px solid',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    {showUserFilters ? <Triangle size={12} style={{width: '16px', paddingRight: '4px'}} /> : <Filter size={16} style={{width: '16px', height: '16px', paddingRight: '4px'}}/>}
                                    {selectedUserIds.length > 0 && (
                                        <span style={{
                                            backgroundColor: 'white',
                                            color: '#333',
                                            borderRadius: '50%',
                                            width: '18px',
                                            height: '18px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '12px',
                                            fontWeight: 'bold'
                                        }}>
                                            {selectedUserIds.length}
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Filter Panel */}
                        {showUserFilters && (
                            <div style={{
                                backgroundColor: 'var(--input-bg)',
                                border: '1px solid #555',
                                borderRadius: '8px',
                                padding: '16px',
                                marginBottom: '20px',
                                marginTop: '-30px'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                    <h3 style={{ margin: 0, fontSize: '16px' }}>Select Users to Display</h3>
                                    <button
                                        onClick={clearAllFilters}
                                        style={{
                                            padding: '4px 12px',
                                            backgroundColor: 'var(--button-bg)',
                                            color: 'var(--button-text)',
                                            border: '1px solid var(--button-bg)',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                        }}
                                    >
                                        Clear Filters
                                    </button>
                                </div>
                                
                                {/* Search Bar */}
                                <div style={{ marginBottom: '16px' }}>
                                    <input
                                        type="text"
                                        placeholder="Search users by name or email..."
                                        value={userSearchQuery}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid var(--input-border)',
                                            borderRadius: '4px',
                                            backgroundColor: 'var(--input-bg)',
                                            color: 'var(--text-color)',
                                            fontSize: '14px',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>
                                
                                {/* User Selection */}
                                <div style={{ 
                                    maxHeight: '300px', 
                                    overflowY: 'auto',
                                    border: '1px solid var(--input-border)',
                                    borderRadius: '4px',
                                    padding: '8px'
                                }}>
                                    <div style={{ marginBottom: '8px', fontSize: '12px', opacity: '0.7' }}>
                                        Select users to filter the list (leave empty to show all):
                                    </div>
                                    {users.map(user => (
                                        <label 
                                            key={user._id} 
                                            style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '8px', 
                                                cursor: 'pointer',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                ':hover': { backgroundColor: 'var(--hover-bg)' }
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedUserIds.includes(user._id)}
                                                onChange={() => handleUserToggle(user._id)}
                                            />
                                            <img 
                                                src={user.picture} 
                                                alt={user.name} 
                                                style={{
                                                    width: '20px', 
                                                    height: '20px', 
                                                    borderRadius: '50%'
                                                }} 
                                            />
                                            <span style={{ fontSize: '14px' }}>{user.name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="users-grid">
                            {(filteredUsers.length > 0 || selectedUserIds.length > 0 || userSearchQuery.trim() ? filteredUsers : users).map(user => (
                                <div 
                                    key={user._id} 
                                    className={`user-card ${selectedUser?._id === user._id ? 'selected' : ''}`}
                                    onClick={() => handleUserSelect(user)}
                                >
                                    <div className="user-info">
                                        <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px'}}>
                                            <img src={user.picture} alt={user.name} style={{width: '20px', height: '20px', borderRadius: '50%', margin: '0px'}} />
                                            <h3 style={{margin: '0', fontSize: '14px'}}>{user.name}</h3>
                                        </div>
                                        <p style={{margin: '0', marginTop: '5px', fontSize: '12px'}}>{user.email}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {selectedUser && renderUserContent()}
                </div>
            )}

            {/* Videos Tab Content */}
            {activeTab === 'videos' && (
                <div className="videos-content">
                    <h2>All Videos</h2>
                    <div className="videos-grid">
                        {videos.map(video => renderVideoCard(video))}
                    </div>
                </div>
            )}

            {/* Add Cut Modal */}
            {selectedVideo && (
                <div className="modal" onClick={(e) => {
                    // Only close if clicking the modal background, not its children
                    if (e.target === e.currentTarget) {
                        setSelectedVideo(null);
                    }
                }}>
                    <div className="modal-content">
                        <h3>Add Cuts for {selectedVideo.title}</h3>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="video/*"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                        <div 
                            className={`upload-area ${isDragging ? 'dragging' : ''}`}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setIsDragging(true);
                            }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleCutDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <div className="upload-label">
                                <Plus size={24} />
                                <span>Drag & drop video cuts here or click to select</span>
                                <span className="upload-hint">Up to 20 files at once</span>
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button type="button" onClick={() => setSelectedVideo(null)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cut Selection Modal */}
            {showCutSelectionModal && (
                <div className="modal" onClick={(e) => {
                    // Only close if clicking the modal background, not its children
                    if (e.target === e.currentTarget) {
                        setShowCutSelectionModal(false);
                        setCurrentScriptForCutSelection(null);
                    }
                }}>
                    <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '80vh', overflow: 'auto' }}>
                        <h3>Select Cut for Text Video</h3>
                        <p style={{ marginBottom: '20px', color: 'var(--text-color)', opacity: '0.8' }}>
                            Choose a specific cut to use for this text-based video instead of a random selection.
                        </p>
                        
                        {availableCuts.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <p>No cuts available for this user.</p>
                                <p style={{ fontSize: '14px', opacity: '0.7' }}>
                                    Upload some cuts in the user's video library first.
                                </p>
                            </div>
                        ) : (
                            <div className="cuts-grid" style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                gap: '16px',
                                marginBottom: '20px'
                            }}>
                                {availableCuts.map((cut) => (
                                    <div 
                                        key={cut._id} 
                                        className="cut-item"
                                        onClick={() => handleConfirmCutSelection(cut._id)}
                                        style={{
                                            border: '2px solid var(--input-border)',
                                            borderRadius: '8px',
                                            padding: '12px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            backgroundColor: 'var(--input-bg)'
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.borderColor = 'var(--button-bg)';
                                            e.currentTarget.style.backgroundColor = 'var(--button-bg)';
                                            e.currentTarget.style.color = 'var(--button-text)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.borderColor = 'var(--input-border)';
                                            e.currentTarget.style.backgroundColor = 'var(--input-bg)';
                                            e.currentTarget.style.color = 'var(--text-color)';
                                        }}
                                    >
                                        <video 
                                            src={cut.url} 
                                            style={{
                                                width: '100%',
                                                height: '120px',
                                                objectFit: 'cover',
                                                borderRadius: '4px',
                                                marginBottom: '8px'
                                            }}
                                            muted
                                            onMouseOver={(e) => e.target.play()}
                                            onMouseOut={(e) => e.target.pause()}
                                        />
                                        <h4 style={{ margin: '0 0 4px 0', fontSize: '14px' }}>{cut.name}</h4>
                                        <p style={{ 
                                            margin: '0', 
                                            fontSize: '12px', 
                                            opacity: '0.7',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {cut.description || 'No description'}
                                        </p>
                                        {cut.autoGenerated && (
                                            <span style={{
                                                fontSize: '10px',
                                                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                                                color: '#8b5cf6',
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                marginTop: '4px',
                                                display: 'inline-block'
                                            }}>
                                                Auto-Generated
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        <div className="modal-actions">
                            <button 
                                type="button" 
                                onClick={() => {
                                    setShowCutSelectionModal(false);
                                    setCurrentScriptForCutSelection(null);
                                }}
                                style={{
                                    backgroundColor: 'var(--input-bg)',
                                    color: 'var(--text-color)',
                                    border: '1px solid var(--input-border)',
                                    borderRadius: '4px',
                                    padding: '8px 16px',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Music Upload Modal */}
            {showMusicUploadModal && (
                <div className="modal" onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowMusicUploadModal(false);
                    }
                }}>
                    <div className="modal-content" style={{ maxWidth: '600px' }}>
                        <h3>Upload Background Music</h3>
                        <p style={{ marginBottom: '20px', color: 'var(--text-color)', opacity: '0.8' }}>
                            Upload music files to the global library for use as background music in videos. Up to 30 files at once. Supported formats: MP3, WAV, AAC, OGG
                        </p>
                        
                        {uploadSuccess ? (
                            <div style={{
                                border: '2px solid #10b981',
                                borderRadius: '8px',
                                padding: '40px',
                                textAlign: 'center',
                                backgroundColor: '#10b981',
                                color: 'white'
                            }}>
                                <div style={{ marginBottom: '16px', fontSize: '48px' }}>
                                    ✅
                                </div>
                                <div style={{ fontSize: '18px', fontWeight: '500', marginBottom: '8px' }}>
                                    Upload Successful!
                                </div>
                                <div style={{ fontSize: '14px', opacity: '0.9' }}>
                                    {uploadSuccessMessage}
                                </div>
                                <div style={{ fontSize: '12px', opacity: '0.7', marginTop: '8px' }}>
                                    Modal will close automatically...
                                </div>
                            </div>
                        ) : isUploadingMusic ? (
                            <div style={{
                                border: '2px solid var(--button-bg)',
                                borderRadius: '8px',
                                padding: '40px',
                                textAlign: 'center',
                                backgroundColor: 'var(--button-bg)',
                                color: 'var(--button-text)'
                            }}>
                                <div style={{ marginBottom: '16px' }}>
                                    <span className="spinner-border" style={{ marginRight: '8px' }} />
                                    Uploading music files...
                                </div>
                                <div style={{ fontSize: '14px', opacity: '0.8' }}>
                                    Processing {uploadProgress.total} files
                                </div>
                                <div style={{ 
                                    width: '100%', 
                                    height: '4px', 
                                    backgroundColor: 'rgba(255,255,255,0.2)', 
                                    borderRadius: '2px',
                                    marginTop: '16px',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        width: `${(uploadProgress.current / uploadProgress.total) * 100}%`,
                                        height: '4px',
                                        backgroundColor: 'white',
                                        transition: 'width 0.3s ease'
                                    }} />
                                </div>
                            </div>
                        ) : (
                            <div 
                                className={`upload-area ${isDraggingMusic ? 'dragging' : ''}`}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    setIsDraggingMusic(true);
                                }}
                                onDragLeave={() => setIsDraggingMusic(false)}
                                onDrop={handleMusicDrop}
                                onClick={() => musicFileInputRef.current?.click()}
                                style={{
                                    border: '2px dashed var(--input-border)',
                                    borderRadius: '8px',
                                    padding: '40px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    backgroundColor: isDraggingMusic ? 'var(--button-bg)' : 'var(--input-bg)',
                                    borderColor: isDraggingMusic ? 'var(--button-bg)' : 'var(--input-border)',
                                    color: isDraggingMusic ? 'var(--button-text)' : 'var(--text-color)'
                                }}
                            >
                                <Upload size={48} style={{ marginBottom: '16px', opacity: '0.7' }} />
                                <div style={{ fontSize: '18px', fontWeight: '500', marginBottom: '8px' }}>
                                    Drag & drop music files here
                                </div>
                                <div style={{ fontSize: '14px', opacity: '0.7', marginBottom: '16px' }}>
                                    or click to select files
                                </div>
                                <div style={{ fontSize: '12px', opacity: '0.6' }}>
                                    Up to 30 files • MP3, WAV, AAC, OGG
                                </div>
                            </div>
                        )}
                        
                        <input
                            ref={musicFileInputRef}
                            type="file"
                            multiple
                            accept="audio/*"
                            onChange={handleMusicFileSelect}
                            style={{ display: 'none' }}
                        />
                        
                        <div className="modal-actions" style={{ marginTop: '20px' }}>
                            <button 
                                type="button" 
                                onClick={() => setShowMusicUploadModal(false)}
                                disabled={isUploadingMusic || uploadSuccess}
                                style={{
                                    backgroundColor: 'var(--input-bg)',
                                    color: 'var(--text-color)',
                                    border: '1px solid var(--input-border)',
                                    borderRadius: '4px',
                                    padding: '8px 16px',
                                    cursor: (isUploadingMusic || uploadSuccess) ? 'not-allowed' : 'pointer',
                                    opacity: (isUploadingMusic || uploadSuccess) ? '0.6' : '1'
                                }}
                            >
                                {isUploadingMusic ? 'Uploading...' : uploadSuccess ? 'Closing...' : 'Cancel'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Music Selection Modal */}
            {showMusicSelectionModal && (
                <div className="modal" onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowMusicSelectionModal(false);
                        setCurrentScriptForMusicSelection(null);
                    }
                }}>
                    <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '80vh', overflow: 'auto' }}>
                        <h3>Select Background Music</h3>
                        <p style={{ marginBottom: '20px', color: 'var(--text-color)', opacity: '0.8' }}>
                            Choose background music from the global library to add to your video. The music will be automatically cut to match the video length.
                        </p>
                        
                        {availableMusic.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <p>No music available in the global library.</p>
                                <p style={{ fontSize: '14px', opacity: '0.7' }}>
                                    Upload some music files first using the "Upload Music" button.
                                </p>
                            </div>
                        ) : (
                            <div className="music-grid" style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                gap: '16px',
                                marginBottom: '20px'
                            }}>
                                {availableMusic.map((music) => (
                                    <div 
                                        key={music._id} 
                                        className="music-item"
                                        onClick={() => handleConfirmMusicSelection(music._id)}
                                        style={{
                                            border: '2px solid var(--input-border)',
                                            borderRadius: '8px',
                                            padding: '16px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            backgroundColor: 'var(--input-bg)'
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.borderColor = 'var(--button-bg)';
                                            e.currentTarget.style.backgroundColor = 'var(--button-bg)';
                                            e.currentTarget.style.color = 'var(--button-text)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.borderColor = 'var(--input-border)';
                                            e.currentTarget.style.backgroundColor = 'var(--input-bg)';
                                            e.currentTarget.style.color = 'var(--text-color)';
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                            <Mic size={20} />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <h4 style={{ 
                                                    margin: '0 0 4px 0', 
                                                    fontSize: '12px', 
                                                    boxSizing: 'border-box', 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis', 
                                                    whiteSpace: 'nowrap',
                                                    width: '100%'
                                                }}>{music.name}</h4>
                                                <p style={{ 
                                                    margin: '0', 
                                                    fontSize: '12px', 
                                                    opacity: '0.7',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    width: '100%'
                                                }}>
                                                    {music.description || 'No description'}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <audio 
                                            src={music.url} 
                                            controls
                                            style={{
                                                width: '100%',
                                                height: '32px'
                                            }}
                                            onPlay={(e) => e.stopPropagation()}
                                            onPause={(e) => e.stopPropagation()}
                                        />
                                        
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center',
                                            marginTop: '8px'
                                        }}>
                                            <span style={{ fontSize: '12px', opacity: '0.7' }}>
                                                {(music.fileSize / 1024 / 1024).toFixed(1)} MB
                                            </span>
                                            <button 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteMusic(music._id);
                                                }}
                                                style={{
                                                    backgroundColor: 'transparent',
                                                    color: 'inherit',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '4px',
                                                    borderRadius: '4px',
                                                    opacity: '0.7',
                                                    transition: 'opacity 0.2s'
                                                }}
                                                onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                                                onMouseOut={(e) => e.currentTarget.style.opacity = '0.7'}
                                            >
                                                <Trash size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        <div className="modal-actions">
                            <button 
                                type="button" 
                                onClick={() => {
                                    setShowMusicSelectionModal(false);
                                    setCurrentScriptForMusicSelection(null);
                                }}
                                style={{
                                    backgroundColor: 'var(--input-bg)',
                                    color: 'var(--text-color)',
                                    border: '1px solid var(--input-border)',
                                    borderRadius: '4px',
                                    padding: '8px 16px',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BackOffice; 